import {
  Building2,
  CalendarDays,
  CalendarPlus,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  List,
  Plus,
  Send,
  Star,
  Trash2,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import {
  createOrganizationCalendarSchedule,
  deleteOrganizationCalendarSchedule,
  getOrganizationCalendarSchedules,
  getOrganizationCalendarTaskAssignees,
  respondOrganizationCalendarTaskRequest,
  updateOrganizationCalendarSchedule,
} from "lib/api/organizationCalendar";
import { requestGlobalNotificationsRefresh } from "lib/notifications/globalNotifications";
import { KOREAN_HOLIDAYS_2026_BY_DATE } from "lib/data/koreanHolidays2026";
import OrganizationScheduleComments from "components/organizations/OrganizationScheduleComments";
import OrganizationMySchedules, {
  OrganizationScheduleHistory,
} from "./OrganizationMySchedules";

const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

const KOREAN_COUNTRY_VALUES = new Set([
  "KR",
  "KOR",
  "KOREA",
  "SOUTH KOREA",
  "REPUBLIC OF KOREA",
  "대한민국",
  "한국",
]);

const STATUS_OPTIONS = [
  { value: "pending", label: "대기" },
  { value: "in_progress", label: "진행중" },
  { value: "completed", label: "완료" },
  { value: "ended", label: "종료" },
  { value: "interrupted", label: "중단" },
];

const PRIORITY_OPTIONS = [
  { value: "1", level: 1, label: "별 1개" },
  { value: "2", level: 2, label: "별 2개" },
  { value: "3", level: 3, label: "별 3개" },
  { value: "4", level: 4, label: "별 4개" },
  { value: "5", level: 5, label: "별 5개" },
];

const TASK_REQUEST_STATUS_LABELS = {
  pending: "수락 대기",
  accepted: "수락됨",
  rejected: "반려됨",
};

const DENSITY_LEGEND_OPTIONS = [
  { level: 1, label: "1건" },
  { level: 2, label: "2건" },
  { level: 3, label: "3건" },
  { level: 4, label: "4건 이상" },
];
const YEAR_HEATMAP_DAY_NUMBERS = Array.from(
  { length: 31 },
  (_, index) => index + 1,
);
const SCHEDULE_SCOPE_ICON_COLOR = "#1d4ed8";

function YearPublicStickerIcon() {
  return (
    <Building2
      aria-hidden="true"
      className="organizationCalendar__yearStickerIcon isPublic"
      color={SCHEDULE_SCOPE_ICON_COLOR}
      size={11}
      strokeWidth={2.2}
    />
  );
}

function YearPersonalStickerIcon() {
  return (
    <UserRound
      aria-hidden="true"
      className="organizationCalendar__yearStickerIcon isPersonal"
      color={SCHEDULE_SCOPE_ICON_COLOR}
      size={10}
      strokeWidth={2.2}
    />
  );
}

const DASHBOARD_COLLAPSED_STORAGE_KEY =
  "organizationCalendar.dashboardCollapsed";
const BOARD_DATE_RANGE_STORAGE_KEY = "organizationCalendar.boardDateRange";
const BOARD_SCHEDULE_FILTER_STORAGE_KEY =
  "organizationCalendar.boardScheduleFilters";
const VIEWER_SCHEDULE_FILTER_STORAGE_KEY =
  "organizationCalendar.viewerScheduleFilters";
const CALENDAR_DISPLAY_MODE_STORAGE_KEY = "organizationCalendar.displayMode";
const CALENDAR_DISPLAY_MODES = new Set(["calendar", "list"]);
const CALENDAR_VIEW_MODE_STORAGE_KEY = "organizationCalendar.viewMode";
const CALENDAR_VIEW_MODES = new Set(["year", "month", "week"]);
const PUBLIC_SCHEDULES_FILTER_ID = "public_schedules";
const MY_SCHEDULES_FILTER_ID = "my_schedules";
const MY_REQUESTS_FILTER_ID = "my_requests";
const MEMBER_FILTER_PREFIX = "member:";
const PUBLIC_SCHEDULES_HEATMAP_ROW = Object.freeze({
  id: "heatmap:public-schedules",
  name: "조직 일정",
  isPublicScheduleRow: true,
});
const DASHBOARD_TRANSITION_MS = 240;
const EMPTY_CALENDAR_LABELS = [];

const LEGACY_PRIORITY_LEVELS = {
  low: 2,
  normal: 3,
  high: 4,
  urgent: 5,
};

const CALENDAR_LABEL_PALETTES = [
  {
    background: "#dbeafe",
    color: "#1e40af",
    border: "rgba(37, 99, 235, 0.3)",
    focus: "rgba(37, 99, 235, 0.18)",
    highlightBorder: "rgba(37, 99, 235, 0.42)",
    hue: 215,
  },
  {
    background: "#ffe4e6",
    color: "#9f1239",
    border: "rgba(225, 29, 72, 0.3)",
    focus: "rgba(225, 29, 72, 0.18)",
    highlightBorder: "rgba(225, 29, 72, 0.42)",
    hue: 347,
  },
  {
    background: "#ccfbf1",
    color: "#115e59",
    border: "rgba(13, 148, 136, 0.3)",
    focus: "rgba(13, 148, 136, 0.18)",
    highlightBorder: "rgba(13, 148, 136, 0.42)",
    hue: 173,
  },
  {
    background: "#fef3c7",
    color: "#92400e",
    border: "rgba(217, 119, 6, 0.31)",
    focus: "rgba(217, 119, 6, 0.18)",
    highlightBorder: "rgba(217, 119, 6, 0.44)",
    hue: 38,
  },
  {
    background: "#ede9fe",
    color: "#5b21b6",
    border: "rgba(124, 58, 237, 0.3)",
    focus: "rgba(124, 58, 237, 0.18)",
    highlightBorder: "rgba(124, 58, 237, 0.42)",
    hue: 263,
  },
  {
    background: "#ecfccb",
    color: "#3f6212",
    border: "rgba(101, 163, 13, 0.31)",
    focus: "rgba(101, 163, 13, 0.18)",
    highlightBorder: "rgba(101, 163, 13, 0.42)",
    hue: 83,
  },
  {
    background: "#cffafe",
    color: "#155e75",
    border: "rgba(8, 145, 178, 0.3)",
    focus: "rgba(8, 145, 178, 0.18)",
    highlightBorder: "rgba(8, 145, 178, 0.42)",
    hue: 188,
  },
  {
    background: "#ffedd5",
    color: "#9a3412",
    border: "rgba(234, 88, 12, 0.3)",
    focus: "rgba(234, 88, 12, 0.18)",
    highlightBorder: "rgba(234, 88, 12, 0.42)",
    hue: 25,
  },
  {
    background: "#e0e7ff",
    color: "#3730a3",
    border: "rgba(79, 70, 229, 0.3)",
    focus: "rgba(79, 70, 229, 0.18)",
    highlightBorder: "rgba(79, 70, 229, 0.42)",
    hue: 239,
  },
  {
    background: "#d1fae5",
    color: "#065f46",
    border: "rgba(5, 150, 105, 0.3)",
    focus: "rgba(5, 150, 105, 0.18)",
    highlightBorder: "rgba(5, 150, 105, 0.42)",
    hue: 158,
  },
  {
    background: "#fce7f3",
    color: "#9d174d",
    border: "rgba(219, 39, 119, 0.3)",
    focus: "rgba(219, 39, 119, 0.18)",
    highlightBorder: "rgba(219, 39, 119, 0.42)",
    hue: 329,
  },
  {
    background: "#e0f2fe",
    color: "#075985",
    border: "rgba(2, 132, 199, 0.3)",
    focus: "rgba(2, 132, 199, 0.18)",
    highlightBorder: "rgba(2, 132, 199, 0.42)",
    hue: 199,
  },
  {
    background: "#fae8ff",
    color: "#86198f",
    border: "rgba(192, 38, 211, 0.3)",
    focus: "rgba(192, 38, 211, 0.18)",
    highlightBorder: "rgba(192, 38, 211, 0.42)",
    hue: 292,
  },
  {
    background: "#fef9c3",
    color: "#854d0e",
    border: "rgba(202, 138, 4, 0.31)",
    focus: "rgba(202, 138, 4, 0.18)",
    highlightBorder: "rgba(202, 138, 4, 0.44)",
    hue: 52,
  },
  {
    background: "#f3e8ff",
    color: "#6b21a8",
    border: "rgba(147, 51, 234, 0.3)",
    focus: "rgba(147, 51, 234, 0.18)",
    highlightBorder: "rgba(147, 51, 234, 0.42)",
    hue: 276,
  },
  {
    background: "#dcfce7",
    color: "#166534",
    border: "rgba(22, 163, 74, 0.3)",
    focus: "rgba(22, 163, 74, 0.18)",
    highlightBorder: "rgba(22, 163, 74, 0.42)",
    hue: 142,
  },
  {
    background: "#fee2e2",
    color: "#991b1b",
    border: "rgba(220, 38, 38, 0.3)",
    focus: "rgba(220, 38, 38, 0.18)",
    highlightBorder: "rgba(220, 38, 38, 0.42)",
    hue: 0,
  },
  {
    background: "#e2e8f0",
    color: "#334155",
    border: "rgba(71, 85, 105, 0.3)",
    focus: "rgba(71, 85, 105, 0.18)",
    highlightBorder: "rgba(71, 85, 105, 0.42)",
    hue: null,
  },
];

const DATE_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const INITIAL_FORM = {
  id: "",
  title: "",
  description: "",
  status: "in_progress",
  achievementRate: "0",
  priority: "3",
  hostUserId: "",
  scheduleType: "personal",
  scheduleScope: "personal",
  requestStatus: "not_required",
  startAt: "",
  endAt: "",
  canEdit: true,
  canDelete: true,
};

function getEditFormForSchedule(schedule) {
  return {
    id: schedule?.id ?? "",
    title: schedule?.title ?? "",
    description: schedule?.description ?? "",
    status: schedule?.status ?? "in_progress",
    achievementRate: String(getScheduleAchievementRate(schedule)),
    priority: normalizePriorityValue(schedule?.priority),
    hostUserId: schedule?.hostUserId ?? "",
    scheduleType: schedule?.scheduleType ?? "personal",
    scheduleScope: schedule?.scheduleScope ?? "personal",
    requestStatus: schedule?.requestStatus ?? "not_required",
    startAt: toDateInputValue(schedule?.startAt),
    endAt: toDateInputValue(schedule?.endAt),
    canEdit: schedule?.canEdit !== false,
    canDelete: schedule?.canDelete !== false,
  };
}

function hasScheduleFormChanges(currentForm, initialForm) {
  if (!currentForm || !initialForm) return false;

  return (
    String(currentForm.title ?? "") !== String(initialForm.title ?? "") ||
    String(currentForm.description ?? "") !==
      String(initialForm.description ?? "") ||
    String(currentForm.status ?? "") !== String(initialForm.status ?? "") ||
    normalizeAchievementRate(currentForm.achievementRate) !==
      normalizeAchievementRate(initialForm.achievementRate) ||
    normalizePriorityValue(currentForm.priority) !==
      normalizePriorityValue(initialForm.priority) ||
    normalizeComparableId(currentForm.hostUserId) !==
      normalizeComparableId(initialForm.hostUserId) ||
    String(currentForm.scheduleType ?? "personal") !==
      String(initialForm.scheduleType ?? "personal") ||
    String(currentForm.scheduleScope ?? "personal") !==
      String(initialForm.scheduleScope ?? "personal") ||
    String(currentForm.startAt ?? "") !== String(initialForm.startAt ?? "") ||
    String(currentForm.endAt ?? "") !== String(initialForm.endAt ?? "")
  );
}

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function hashScheduleColorSeed(value) {
  const text = String(value ?? "");
  let hash = 2166136261;

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function getScheduleCalendarColorKey(schedule) {
  return String(
    schedule?.id ||
      [schedule?.title, schedule?.startAt, schedule?.endAt, schedule?.createdAt]
        .filter(Boolean)
        .join("|"),
  );
}

function getScheduleCalendarPaletteStyle(palette) {
  return {
    "--calendar-label-bg": palette.background,
    "--calendar-label-color": palette.color,
    "--calendar-label-border": palette.border,
    "--calendar-bar-focus-color": palette.focus,
    "--calendar-highlight-border": palette.highlightBorder,
  };
}

export function getDefaultScheduleCalendarColorStyle(schedule) {
  const colorKey = getScheduleCalendarColorKey(schedule);
  const palette =
    CALENDAR_LABEL_PALETTES[
      hashScheduleColorSeed(colorKey) % CALENDAR_LABEL_PALETTES.length
    ];

  return getScheduleCalendarPaletteStyle(palette);
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeek(date) {
  return addDays(startOfDay(date), -date.getDay());
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

function addYears(date, years) {
  const targetYear = date.getFullYear() + years;
  const month = date.getMonth();
  const lastDayOfTargetMonth = new Date(targetYear, month + 1, 0).getDate();

  return new Date(
    targetYear,
    month,
    Math.min(date.getDate(), lastDayOfTargetMonth),
  );
}

function formatDateKey(date) {
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
}

function parseDateKey(value) {
  const text = String(value ?? "").trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );

  return formatDateKey(date) === text ? date : null;
}

function getCurrentMonthDateRange(referenceDate = new Date()) {
  const from = startOfMonth(referenceDate);
  const to = addDays(addMonths(from, 1), -1);

  return {
    from: formatDateKey(from),
    to: formatDateKey(to),
  };
}

function isValidBoardDateRange(range) {
  const from = parseDateKey(range?.from);
  const to = parseDateKey(range?.to);
  return Boolean(from && to && from <= to);
}

function getStoredBoardDateRange() {
  const fallbackRange = getCurrentMonthDateRange();
  if (typeof window === "undefined") return fallbackRange;

  try {
    const savedRange = JSON.parse(
      window.localStorage.getItem(BOARD_DATE_RANGE_STORAGE_KEY) || "null",
    );
    return isValidBoardDateRange(savedRange) ? savedRange : fallbackRange;
  } catch {
    return fallbackRange;
  }
}

function persistBoardDateRange(range) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      BOARD_DATE_RANGE_STORAGE_KEY,
      JSON.stringify(range),
    );
  } catch {
    // Ignore storage failures; the applied range remains available in memory.
  }
}

function getKoreanHolidayName(date) {
  const dateKey = formatDateKey(date);
  return KOREAN_HOLIDAYS_2026_BY_DATE[dateKey] ?? "";
}

function normalizeCountryValue(value) {
  return String(value ?? "")
    .trim()
    .toUpperCase();
}

function isKoreanCountry(...values) {
  return values
    .map(normalizeCountryValue)
    .some((value) => KOREAN_COUNTRY_VALUES.has(value));
}

function formatYearMonthLabel(date) {
  return `${date.getFullYear()}.${padDatePart(date.getMonth() + 1)}`;
}

function formatKstDateKey(date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "2-digit",
    timeZone: "Asia/Seoul",
    year: "numeric",
  }).formatToParts(date);
  const byType = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );

  return `${byType.year}-${byType.month}-${byType.day}`;
}

function toDateInputValue(value) {
  if (!value) return "";

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? "" : formatDateKey(value);
  }

  const text = String(value).trim();
  const dateMatch = text.match(DATE_INPUT_PATTERN);
  if (dateMatch) return dateMatch[0];

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";

  return formatKstDateKey(date);
}

function formatScheduleDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    weekday: "short",
    timeZone: "Asia/Seoul",
  }).format(date);
}

function formatScheduleRange(schedule) {
  const start = toDateInputValue(schedule?.startAt);
  const end = toDateInputValue(schedule?.endAt);

  if (!start && !end) return "기간 미정";
  if (!start) return `시작일 미정 - ${formatScheduleDate(end)}`;
  if (!end) return `${formatScheduleDate(start)} - 마감일 미정`;

  return `${formatScheduleDate(start)} - ${formatScheduleDate(end)}`;
}

function formatCompactScheduleDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    timeZone: "Asia/Seoul",
  }).format(date);
}

function formatCompactScheduleRange(schedule) {
  const start = toDateInputValue(schedule?.startAt);
  const end = toDateInputValue(schedule?.endAt);

  if (!start && !end) return "기간 미정";
  if (!start) return `시작일 미정 - ${formatCompactScheduleDate(end)}`;
  if (!end) return `${formatCompactScheduleDate(start)} - 마감일 미정`;
  if (start === end) return formatCompactScheduleDate(start);

  return `${formatCompactScheduleDate(start)} - ${formatCompactScheduleDate(end)}`;
}

function getScheduleOwnerName(schedule) {
  return schedule?.hostUserName || "담당자 없음";
}

function getScheduleRegistrarName(schedule) {
  return schedule?.createdByName || schedule?.hostUserName || "등록자 없음";
}

function getScheduleRegistrarKey(schedule) {
  return (
    normalizeComparableId(getScheduleCreatorId(schedule)) ||
    normalizeSearchText(getScheduleRegistrarName(schedule))
  );
}

function getScheduleRegistrarNames(schedules) {
  const registrarMap = new Map();

  schedules.forEach((schedule) => {
    const key = getScheduleRegistrarKey(schedule);
    const name = String(getScheduleRegistrarName(schedule) ?? "").trim();
    if (!key || !name) return;
    if (!registrarMap.has(key)) registrarMap.set(key, name);
  });

  return Array.from(registrarMap.values());
}

function getStatusLabel(value) {
  return (
    STATUS_OPTIONS.find((option) => option.value === value)?.label ?? "진행중"
  );
}

function isTaskRequestSchedule(schedule) {
  return schedule?.scheduleType === "task_request";
}

function isOrganizationWideSchedule(schedule) {
  return schedule?.scheduleScope === "organization";
}

function getScheduleScopeLabel(schedule) {
  return isOrganizationWideSchedule(schedule) ? "조직 일정" : "개인 일정";
}

function getTaskRequestStatusLabel(value) {
  return TASK_REQUEST_STATUS_LABELS[value] ?? "수락 대기";
}

function normalizeAchievementRate(value, fallback = 0) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;

  return Math.min(100, Math.max(0, Math.round(number)));
}

function getScheduleAchievementRate(schedule) {
  return normalizeAchievementRate(
    schedule?.achievementRate ?? schedule?.achievement_rate,
  );
}

function formatAchievementRate(value) {
  return `${normalizeAchievementRate(value)}%`;
}

function getPriorityLevel(value) {
  const legacyLevel = LEGACY_PRIORITY_LEVELS[String(value ?? "").trim()];
  if (legacyLevel) return legacyLevel;

  const numericLevel = Number(value);
  if (Number.isFinite(numericLevel)) {
    return Math.min(5, Math.max(1, Math.round(numericLevel)));
  }

  return 3;
}

function normalizePriorityValue(value) {
  return String(getPriorityLevel(value));
}

function getPriorityClassName(value) {
  return `is-level-${getPriorityLevel(value)}`;
}

function getPriorityLabel(value) {
  return `별 ${getPriorityLevel(value)}개`;
}

function PriorityStars({ value }) {
  const level = getPriorityLevel(value);

  return (
    <span className="organizationCalendar__priorityStars" aria-hidden="true">
      <Star size={12} strokeWidth={1.8} />
      <span>{level}</span>
    </span>
  );
}

function PriorityBadge({ value }) {
  return (
    <span
      aria-label={getPriorityLabel(value)}
      className={`organizationCalendar__priorityBadge ${getPriorityClassName(
        value,
      )}`}
      title={getPriorityLabel(value)}
    >
      <PriorityStars value={value} />
    </span>
  );
}

function ScheduleScopeBadge({ schedule }) {
  const isOrganizationWide = isOrganizationWideSchedule(schedule);
  const organizationLabel = String(schedule?.organizationName ?? "").trim();

  return (
    <span
      className={`organizationCalendar__scopeBadge ${
        isOrganizationWide ? "isOrganization" : "isPersonal"
      }`}
    >
      {isOrganizationWide ? (
        <Building2
          aria-hidden="true"
          color={SCHEDULE_SCOPE_ICON_COLOR}
          size={11}
        />
      ) : (
        <UserRound
          aria-hidden="true"
          color={SCHEDULE_SCOPE_ICON_COLOR}
          size={11}
        />
      )}
      {organizationLabel ? `${organizationLabel} · ` : ""}
      {getScheduleScopeLabel(schedule)}
    </span>
  );
}

function HeatmapScheduleTitle({ schedule, title }) {
  const isOrganizationWide = isOrganizationWideSchedule(schedule);
  const ScopeIcon = isOrganizationWide ? Building2 : UserRound;

  return (
    <>
      <ScopeIcon
        aria-hidden="true"
        className="organizationCalendar__heatmapScheduleScopeIcon"
        color={SCHEDULE_SCOPE_ICON_COLOR}
        size={12}
        strokeWidth={2.2}
      />
      <strong>{title}</strong>
      {!isOrganizationWide && (
        <span className="organizationCalendar__heatmapScheduleAuthor">
          {getScheduleRegistrarName(schedule)}
        </span>
      )}
    </>
  );
}

function AchievementMeter({ value }) {
  const rate = normalizeAchievementRate(value);

  return (
    <span
      aria-label={`달성률 ${rate}%`}
      className="organizationCalendar__achievementMeter"
      title={`달성률 ${rate}%`}
    >
      <span
        className="organizationCalendar__achievementTrack"
        style={{ "--achievement-rate": `${rate}%` }}
      >
        <span />
      </span>
      <strong>{rate}%</strong>
    </span>
  );
}

const OrganizationCalendarBoard = memo(function OrganizationCalendarBoard({
  columns,
  isLoading,
  onOpenSchedule,
}) {
  return (
    <div className="organizationCalendar__board" aria-busy={isLoading}>
      {isLoading ? (
        <div className="organizationCalendar__boardLoading">
          <span className="organizationCalendar__loadingLine" />
          <span className="organizationCalendar__loadingLine" />
          <span className="organizationCalendar__loadingLine" />
        </div>
      ) : (
        columns.map((column) => (
          <section
            className={`organizationCalendar__boardColumn is-${column.value}`}
            key={column.value}
          >
            <div className="organizationCalendar__boardColumnHeader">
              <span
                className={`organizationCalendar__statusBadge is-${column.value}`}
              >
                {column.label}
              </span>
              <strong>{column.schedules.length}건</strong>
            </div>

            <div className="organizationCalendar__boardList">
              {column.schedules.map((schedule) => (
                <button
                  className={[
                    "organizationCalendar__boardCard",
                    `is-${schedule.status}`,
                  ].join(" ")}
                  key={schedule.id}
                  onClick={() => onOpenSchedule(schedule)}
                  type="button"
                >
                  <span className="organizationCalendar__boardCardHeader">
                    <strong>{schedule.title}</strong>
                    <span className="organizationCalendar__boardCardCreatedMeta">
                      <small>{getScheduleRegistrarName(schedule)}</small>
                    </span>
                    <PriorityBadge value={schedule.priority} />
                  </span>
                  <span className="organizationCalendar__boardCardMeta">
                    <ScheduleScopeBadge schedule={schedule} />
                    <small>{formatScheduleRange(schedule)}</small>
                  </span>
                  <AchievementMeter
                    value={getScheduleAchievementRate(schedule)}
                  />
                </button>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
});

const OrganizationCalendarYearView = memo(
  function OrganizationCalendarYearView({
    canCreateSchedule,
    isLoading,
    months,
    onOpenCreate,
    onOpenSchedule,
  }) {
    const [openMonthKeys, setOpenMonthKeys] = useState(() => new Set());

    const toggleMonthScheduleList = useCallback((monthKey) => {
      setOpenMonthKeys((previousMonthKeys) => {
        const nextMonthKeys = new Set(previousMonthKeys);

        if (nextMonthKeys.has(monthKey)) {
          nextMonthKeys.delete(monthKey);
        } else {
          nextMonthKeys.add(monthKey);
        }

        return nextMonthKeys;
      });
    }, []);

    return (
      <>
        <div
          aria-busy={isLoading}
          className="organizationCalendar__yearGrid"
        >
          {months.map((month) => {
            const isScheduleListOpen = openMonthKeys.has(month.monthKey);
            const scheduleListId = `organization-calendar-${month.monthKey}-schedule-list`;

            return (
              <section
                className={`organizationCalendar__yearMonth ${
                  month.isCurrentMonth ? "isCurrentMonth" : ""
                } ${
                  isScheduleListOpen
                    ? "isScheduleListOpen"
                    : "isScheduleListClosed"
                }`}
                key={month.monthKey}
              >
                <div className="organizationCalendar__yearMonthSummaryHeader">
                  <div>
                    <strong>{month.monthLabel}</strong>
                  </div>
                  <button
                    aria-label={`${month.monthLabel} 일정 등록`}
                    className="organizationCalendar__addDayButton organizationCalendar__yearMonthAddButton"
                    disabled={!canCreateSchedule}
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenCreate(month.date);
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                    title={
                      canCreateSchedule
                        ? `${month.monthLabel} 일정 등록`
                        : "내 소속 페이지에서만 일정을 등록할 수 있습니다."
                    }
                    type="button"
                  >
                    <Plus size={15} aria-hidden="true" />
                  </button>
                </div>

                <div
                  aria-hidden="true"
                  className="organizationCalendar__yearMiniCalendar"
                >
                  <div className="organizationCalendar__yearMiniWeekdays">
                    {WEEKDAY_LABELS.map((weekday, index) => (
                      <span
                        className={
                          index === 0
                            ? "isSunday"
                            : index === 6
                              ? "isSaturday"
                              : ""
                        }
                        key={weekday}
                      >
                        {weekday}
                      </span>
                    ))}
                  </div>
                  <div className="organizationCalendar__yearMiniDays">
                    {month.calendarCells.map((cell) => (
                      <span
                        className={[
                          !cell.date ? "isEmpty" : "",
                          cell.date?.getDay() === 0 ? "isSunday" : "",
                          cell.date?.getDay() === 6 ? "isSaturday" : "",
                          cell.isToday ? "isToday" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        key={cell.key}
                      >
                        {cell.date?.getDate() ?? ""}
                        {(cell.publicScheduleCount > 0 ||
                          cell.personalScheduleCount > 0) && (
                          <span className="organizationCalendar__yearMiniStickers">
                            {cell.publicScheduleCount > 0 && (
                              <span className="isPublic">
                                {cell.publicScheduleCount <= 4 ? (
                                  Array.from(
                                    { length: cell.publicScheduleCount },
                                    (_, index) => (
                                      <YearPublicStickerIcon
                                        key={`public-${index}`}
                                      />
                                    ),
                                  )
                                ) : (
                                  <>
                                    <YearPublicStickerIcon />
                                    <b>×{cell.publicScheduleCount}</b>
                                  </>
                                )}
                              </span>
                            )}
                            {cell.personalScheduleCount > 0 && (
                              <span
                                className={`isPersonal ${
                                  cell.personalScheduleCount > 7
                                    ? "isCount"
                                    : ""
                                }`}
                              >
                                {cell.personalScheduleCount <= 7 ? (
                                  Array.from(
                                    { length: cell.personalScheduleCount },
                                    (_, index) => (
                                      <YearPersonalStickerIcon
                                        key={`personal-${index}`}
                                      />
                                    ),
                                  )
                                ) : (
                                  <>
                                    <YearPersonalStickerIcon />
                                    <b>×{cell.personalScheduleCount}</b>
                                  </>
                                )}
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>

                <button
                  aria-controls={scheduleListId}
                  aria-expanded={isScheduleListOpen}
                  aria-label={`${month.monthLabel} 일정 목록 ${
                    isScheduleListOpen ? "닫기" : "열기"
                  }`}
                  className="organizationCalendar__yearMonthBlindToggle"
                  onClick={() => toggleMonthScheduleList(month.monthKey)}
                  title={`일정 목록 ${isScheduleListOpen ? "닫기" : "열기"}`}
                  type="button"
                >
                  {isScheduleListOpen ? (
                    <ChevronRight aria-hidden="true" size={16} />
                  ) : (
                    <ChevronLeft aria-hidden="true" size={16} />
                  )}
                </button>

                <div
                  aria-hidden={!isScheduleListOpen}
                  className={`organizationCalendar__yearMonthBody ${
                    isScheduleListOpen ? "isOpen" : "isClosed"
                  }`}
                  id={scheduleListId}
                >
                  <header className="organizationCalendar__yearMonthBodyHeader">
                    <span className="organizationCalendar__yearMonthSummaryCounts">
                      <span className="isPublic">
                        <YearPublicStickerIcon />
                        조직 {month.publicSchedules.length}건
                      </span>
                      <span className="isPersonal">
                        <YearPersonalStickerIcon />
                        개인 {month.privateScheduleCount}건
                      </span>
                    </span>
                  </header>

                  {isLoading ? (
                    <>
                      <span className="organizationCalendar__loadingLine" />
                      <span className="organizationCalendar__loadingLine" />
                      <span className="organizationCalendar__loadingLine" />
                    </>
                  ) : month.schedules.length > 0 ? (
                    <div className="organizationCalendar__yearMonthSchedules">
                      {month.schedules.map((schedule) => (
                        <button
                          aria-label={`${schedule.title} 일정 상세`}
                          key={schedule.id}
                          onClick={() => onOpenSchedule(schedule)}
                          type="button"
                        >
                          <strong>{schedule.title}</strong>
                          <span className="organizationCalendar__yearMonthScheduleMeta">
                            <span>
                              {isOrganizationWideSchedule(schedule) ? (
                                <Building2
                                  aria-hidden="true"
                                  color={SCHEDULE_SCOPE_ICON_COLOR}
                                  size={9}
                                />
                              ) : (
                                <UserRound
                                  aria-hidden="true"
                                  color={SCHEDULE_SCOPE_ICON_COLOR}
                                  size={9}
                                />
                              )}
                              {getSchedulePersonName(schedule)}
                            </span>
                            <small>
                              {formatCompactScheduleRange(schedule)}
                            </small>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="organizationCalendar__yearMonthEmpty">
                      일정 없음
                    </p>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </>
    );
  },
);

const OrganizationCalendarYearHeatmap = memo(
  function OrganizationCalendarYearHeatmap({
    expandedMonthKeys,
    onOpenDay,
    onOpenSchedule,
    onToggleMonth,
    rows,
    todayKey,
  }) {
    return (
      <div className="organizationCalendar__heatmapWrap">
        <table className="organizationCalendar__heatmap isYear">
          <thead>
            <tr>
              <th
                className="organizationCalendar__heatmapMemberHeader"
                scope="col"
              >
                월 / 일정
              </th>
              {YEAR_HEATMAP_DAY_NUMBERS.map((dayNumber) => (
                <th
                  className="organizationCalendar__heatmapDateHeader"
                  key={dayNumber}
                  scope="col"
                >
                  <span>{dayNumber}</span>
                  <small>일</small>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isExpanded = expandedMonthKeys.includes(row.monthKey);

              return (
                <Fragment key={row.monthKey}>
                  <tr
                    className={[
                      "organizationCalendar__heatmapMemberRow",
                      "isYearMonth",
                      isExpanded ? "isExpanded" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <th
                      className={[
                        "organizationCalendar__heatmapMember",
                        isExpanded ? "isExpanded" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      scope="row"
                    >
                      <div className="organizationCalendar__heatmapMemberSummary">
                        <button
                          aria-expanded={isExpanded}
                          className="organizationCalendar__heatmapMemberName"
                          onClick={() => onToggleMonth(row.monthKey)}
                          type="button"
                        >
                          <strong>{row.monthLabel}</strong>
                        </button>
                        <span className="organizationCalendar__heatmapMemberCount">
                          {row.totalCount}건
                        </span>
                      </div>
                    </th>

                    {row.cells.map((cell) => {
                      if (!cell.date) {
                        return (
                          <td
                            aria-hidden="true"
                            className="organizationCalendar__heatmapCell isUnavailable"
                            key={`${row.monthKey}-${cell.dayNumber}`}
                          />
                        );
                      }

                      const count = cell.schedules.length;
                      const isTodayCell = cell.dateKey === todayKey;
                      const titles = cell.schedules
                        .map((schedule) => schedule.title)
                        .filter(Boolean)
                        .join(", ");
                      const label = `${row.monthLabel} ${cell.dayNumber}일 일정 ${count}건`;

                      return (
                        <td
                          className={[
                            "organizationCalendar__heatmapCell",
                            `isLevel-${cell.level}`,
                            isTodayCell ? "isToday" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          key={`${row.monthKey}-${cell.dayNumber}`}
                        >
                          <button
                            aria-label={label}
                            onClick={() => onOpenDay(cell.date)}
                            title={titles || label}
                            type="button"
                          >
                            <span>{count > 0 ? count : ""}</span>
                          </button>
                        </td>
                      );
                    })}
                  </tr>

                  {isExpanded && row.schedules.length === 0 && (
                    <tr className="organizationCalendar__heatmapScheduleRow isEmpty">
                      <th
                        className="organizationCalendar__heatmapSchedule isEmpty"
                        scope="row"
                      >
                        <span>일정 없음</span>
                      </th>
                      <td
                        className="organizationCalendar__heatmapScheduleEmpty"
                        colSpan={YEAR_HEATMAP_DAY_NUMBERS.length}
                      >
                        {row.monthLabel}에 일정이 없습니다.
                      </td>
                    </tr>
                  )}

                  {isExpanded
                    ? row.schedules.map((schedule) => {
                        const status = schedule.status ?? "in_progress";
                        const scheduleId = String(
                          schedule.id ??
                            `${row.monthKey}-${schedule.title}-${schedule.startAt}`,
                        );
                        const scheduleTitle =
                          String(schedule.title ?? "").trim() || "일정";

                        return (
                          <tr
                            className={[
                              "organizationCalendar__heatmapScheduleRow",
                              `is-${status}`,
                            ].join(" ")}
                            key={`${row.monthKey}-${scheduleId}`}
                          >
                            <th
                              className="organizationCalendar__heatmapSchedule"
                              scope="row"
                            >
                              <button
                                className="organizationCalendar__heatmapScheduleSummary"
                                onClick={() => onOpenSchedule(schedule)}
                                type="button"
                              >
                                <HeatmapScheduleTitle
                                  schedule={schedule}
                                  title={scheduleTitle}
                                />
                              </button>
                            </th>

                            {row.cells.map((cell) => {
                              if (!cell.date) {
                                return (
                                  <td
                                    aria-hidden="true"
                                    className="organizationCalendar__heatmapScheduleCell isUnavailable"
                                    key={`${scheduleId}-${cell.dayNumber}`}
                                  />
                                );
                              }

                              const isActive = scheduleOverlapsDay(
                                schedule,
                                cell.date,
                              );
                              const isTodayCell = cell.dateKey === todayKey;
                              const label = isActive
                                ? `${scheduleTitle} ${row.monthLabel} ${cell.dayNumber}일 ${getStatusLabel(status)}`
                                : `${scheduleTitle} ${row.monthLabel} ${cell.dayNumber}일 일정 없음`;

                              return (
                                <td
                                  className={[
                                    "organizationCalendar__heatmapScheduleCell",
                                    isTodayCell ? "isToday" : "",
                                    isActive ? `is-${status}` : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  key={`${scheduleId}-${cell.dayNumber}`}
                                >
                                  <button
                                    aria-label={label}
                                    disabled={!isActive}
                                    onClick={() => onOpenSchedule(schedule)}
                                    title={
                                      isActive
                                        ? `${scheduleTitle} · ${getStatusLabel(
                                            status,
                                          )}`
                                        : ""
                                    }
                                    type="button"
                                  >
                                    <span>
                                      {isActive ? getStatusLabel(status) : ""}
                                    </span>
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })
                    : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  },
);

const CalendarScheduleFilterDropdown = memo(
  function CalendarScheduleFilterDropdown({
    idPrefix,
    options,
    selectedIds,
    onChange,
  }) {
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef(null);
    const allOptionIds = useMemo(
      () => options.map((option) => option.id),
      [options],
    );
    const appliedSelectedIds = useMemo(
      () => {
        if (selectedIds == null) return allOptionIds;

        const selectedIdSet = new Set(selectedIds);
        return allOptionIds.filter((optionId) => selectedIdSet.has(optionId));
      },
      [allOptionIds, selectedIds],
    );
    const [draftSelectedIds, setDraftSelectedIds] = useState(
      appliedSelectedIds,
    );
    const appliedSelectedIdSet = useMemo(
      () => new Set(appliedSelectedIds),
      [appliedSelectedIds],
    );
    const draftSelectedIdSet = useMemo(
      () => new Set(draftSelectedIds),
      [draftSelectedIds],
    );
    const appliedSelectedOptions = options.filter((option) =>
      appliedSelectedIdSet.has(option.id),
    );
    const triggerLabel =
      appliedSelectedOptions.length === 0
        ? "일정 선택 없음"
        : appliedSelectedOptions.length === options.length
          ? "전체 일정"
          : appliedSelectedOptions.length === 1
            ? appliedSelectedOptions[0].label
            : `${appliedSelectedOptions[0].label} 외 ${appliedSelectedOptions.length - 1}개`;
    const isAllDraftSelected =
      options.length > 0 && draftSelectedIds.length === options.length;

    useEffect(() => {
      if (!isOpen) setDraftSelectedIds(appliedSelectedIds);
    }, [appliedSelectedIds, isOpen]);

    useEffect(() => {
      if (!isOpen) return undefined;

      const handlePointerDown = (event) => {
        if (!rootRef.current?.contains(event.target)) setIsOpen(false);
      };
      const handleKeyDown = (event) => {
        if (event.key === "Escape") setIsOpen(false);
      };

      document.addEventListener("mousedown", handlePointerDown);
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handlePointerDown);
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [isOpen]);

    const handleToggleMenu = () => {
      if (isOpen) {
        setIsOpen(false);
        return;
      }

      setDraftSelectedIds(appliedSelectedIds);
      setIsOpen(true);
    };

    const handleToggleOption = (optionId) => {
      setDraftSelectedIds((currentSelectedIds) =>
        currentSelectedIds.includes(optionId)
          ? currentSelectedIds.filter(
              (selectedOptionId) => selectedOptionId !== optionId,
            )
          : allOptionIds.filter(
              (availableOptionId) =>
                availableOptionId === optionId ||
                currentSelectedIds.includes(availableOptionId),
            ),
      );
    };

    const handleToggleAll = () => {
      setDraftSelectedIds(isAllDraftSelected ? [] : allOptionIds);
    };

    const handleApply = () => {
      onChange(isAllDraftSelected ? null : draftSelectedIds);
      setIsOpen(false);
    };

    return (
      <div className="organizationCalendar__scheduleFilter" ref={rootRef}>
        <button
          aria-controls={`${idPrefix}-schedule-filter-menu`}
          aria-expanded={isOpen}
          className={isOpen ? "is-open" : ""}
          onClick={handleToggleMenu}
          type="button"
        >
          <UserRound aria-hidden="true" size={14} strokeWidth={2.2} />
          <span>{triggerLabel}</span>
          <ChevronDown aria-hidden="true" size={14} />
        </button>

        {isOpen && (
          <div
            aria-label="일정 표시 대상 선택"
            className="organizationCalendar__scheduleFilterMenu"
            id={`${idPrefix}-schedule-filter-menu`}
            role="group"
          >
            <div className="organizationCalendar__scheduleFilterMenuHeader">
              <strong>표시할 일정</strong>
              <span>{draftSelectedIds.length}개 선택</span>
            </div>
            <ul>
              <li className="is-all-option">
                <label>
                  <input
                    checked={isAllDraftSelected}
                    onChange={handleToggleAll}
                    type="checkbox"
                  />
                  <span
                    aria-hidden="true"
                    className="organizationCalendar__scheduleFilterCheck"
                  >
                    {isAllDraftSelected && <Check size={11} strokeWidth={3} />}
                  </span>
                  <span>전체 선택</span>
                </label>
              </li>
              {options.map((option) => {
                const checked = draftSelectedIdSet.has(option.id);
                return (
                  <li key={option.id}>
                    <label>
                      <input
                        checked={checked}
                        onChange={() => handleToggleOption(option.id)}
                        type="checkbox"
                      />
                      <span
                        aria-hidden="true"
                        className="organizationCalendar__scheduleFilterCheck"
                      >
                        {checked && <Check size={11} strokeWidth={3} />}
                      </span>
                      <span>{option.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="organizationCalendar__scheduleFilterMenuActions">
              <button onClick={handleApply} type="button">
                적용
              </button>
            </div>
          </div>
        )}
      </div>
    );
  },
);

const CalendarOrganizationScopeSelect = memo(
  function CalendarOrganizationScopeSelect({ options, value, onChange }) {
    const rootRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption =
      options.find((option) => option.id === value) ?? options[0];

    useEffect(() => {
      if (!isOpen) return undefined;

      const handlePointerDown = (event) => {
        if (!rootRef.current?.contains(event.target)) setIsOpen(false);
      };
      const handleKeyDown = (event) => {
        if (event.key === "Escape") setIsOpen(false);
      };

      document.addEventListener("mousedown", handlePointerDown);
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handlePointerDown);
        window.removeEventListener("keydown", handleKeyDown);
      };
    }, [isOpen]);

    if (options.length <= 2) return null;

    return (
      <div
        className="organizationPage__sortControl organizationCalendar__organizationScopeFilter"
        ref={rootRef}
      >
        <span>소속</span>
        <button
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label="일정 소속 범위"
          className={[
            "organizationCalendar__organizationScopeTrigger",
            isOpen ? "is-open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setIsOpen((currentValue) => !currentValue)}
          type="button"
        >
          <strong>{selectedOption?.label ?? "소속 선택"}</strong>
          <ChevronDown aria-hidden="true" size={14} />
        </button>

        {isOpen && (
          <div
            aria-label="일정 소속 범위 선택"
            className="organizationCalendar__organizationScopeMenu"
            role="listbox"
          >
            {options.map((option) => {
              const isSelected = option.id === value;

              return (
                <button
                  aria-selected={isSelected}
                  className={isSelected ? "is-selected" : ""}
                  key={option.id}
                  onClick={() => {
                    onChange(option.id);
                    setIsOpen(false);
                  }}
                  role="option"
                  type="button"
                >
                  <span>{option.label}</span>
                  {isSelected && <Check aria-hidden="true" size={14} />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);

export const CalendarScheduleLabels = memo(function CalendarScheduleLabels({
  dayLabels,
  hoveredScheduleId,
  isReadOnly = false,
  onHoverSchedule,
  onOpenSchedule,
  resolveScheduleColorStyle,
  showAll = false,
}) {
  const labels = dayLabels?.labels ?? EMPTY_CALENDAR_LABELS;
  const viewportRef = useRef(null);
  const measurementRef = useRef(null);
  const [labelLayout, setLabelLayout] = useState(() => ({
    rows: labels.length > 0 ? [labels.map((_, index) => index)] : [],
    showOverflow: false,
  }));

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const measurement = measurementRef.current;
    if (!viewport || !measurement) return undefined;

    let animationFrameId = 0;
    let isActive = true;
    const measureLabelLayout = () => {
      if (!isActive) return;

      const labelElements = Array.from(
        measurement.querySelectorAll("[data-calendar-measure-label]"),
      );
      const overflowElement = measurement.querySelector(
        "[data-calendar-measure-overflow]",
      );

      const availableHeight = Math.max(0, viewport.clientHeight - 1);
      const availableWidth = Math.max(0, viewport.clientWidth - 1);
      if (
        (!showAll && availableHeight <= 0) ||
        availableWidth <= 0 ||
        labelElements.length === 0 ||
        !overflowElement
      ) {
        setLabelLayout({ rows: [], showOverflow: false });
        return;
      }

      labelElements.forEach((element) => {
        element.hidden = false;
      });
      overflowElement.hidden = true;

      const measurementStyle = window.getComputedStyle(measurement);
      const columnGap = Number.parseFloat(measurementStyle.columnGap) || 0;
      const rowGap = Number.parseFloat(measurementStyle.rowGap) || 0;
      const labelHeight = Math.max(
        ...labelElements.map((element) =>
          Math.ceil(element.getBoundingClientRect().height),
        ),
      );
      const maximumRowCount = showAll
        ? labelElements.length
        : Math.max(
            0,
            Math.floor((availableHeight + rowGap) / (labelHeight + rowGap)),
          );
      const labelWidths = labelElements.map((element) =>
        Math.min(
          availableWidth,
          Math.ceil(element.getBoundingClientRect().width),
        ),
      );

      const packRows = (reservedLastRowWidth = 0) => {
        const rows = Array.from({ length: maximumRowCount }, () => []);
        const remainingWidths = rows.map((_, rowIndex) =>
          Math.max(
            0,
            availableWidth -
              (rowIndex === maximumRowCount - 1 ? reservedLastRowWidth : 0),
          ),
        );

        labelWidths.forEach((labelWidth, labelIndex) => {
          for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
            const requiredWidth =
              labelWidth + (rows[rowIndex].length > 0 ? columnGap : 0);
            if (requiredWidth > remainingWidths[rowIndex]) continue;

            rows[rowIndex].push(labelIndex);
            remainingWidths[rowIndex] -= requiredWidth;
            break;
          }
        });

        return rows;
      };

      let rows = packRows();
      let visibleCount = rows.reduce((count, row) => count + row.length, 0);
      let showOverflow = !showAll && visibleCount < labelElements.length;

      if (showOverflow && maximumRowCount > 0) {
        overflowElement.hidden = false;

        for (let attempt = 0; attempt < 3; attempt += 1) {
          overflowElement.textContent = `+${labelElements.length - visibleCount}`;
          const overflowWidth = Math.ceil(
            overflowElement.getBoundingClientRect().width,
          );

          if (overflowWidth > availableWidth) {
            showOverflow = false;
            break;
          }

          const nextRows = packRows(overflowWidth + columnGap);
          const nextVisibleCount = nextRows.reduce(
            (count, row) => count + row.length,
            0,
          );
          rows = nextRows;
          if (nextVisibleCount === visibleCount) break;
          visibleCount = nextVisibleCount;
        }
      }

      const nextRows = showOverflow
        ? rows
        : rows.filter((row) => row.length > 0);
      setLabelLayout((currentLayout) => {
        const hasSameRows =
          currentLayout.rows.length === nextRows.length &&
          currentLayout.rows.every(
            (row, rowIndex) =>
              row.length === nextRows[rowIndex].length &&
              row.every(
                (labelIndex, index) => labelIndex === nextRows[rowIndex][index],
              ),
          );

        return hasSameRows && currentLayout.showOverflow === showOverflow
          ? currentLayout
          : { rows: nextRows, showOverflow };
      });
    };

    const scheduleMeasurement = () => {
      if (!isActive) return;
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(measureLabelLayout);
    };

    measureLabelLayout();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleMeasurement)
        : null;
    resizeObserver?.observe(viewport);
    if (!resizeObserver) {
      window.addEventListener("resize", scheduleMeasurement);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(scheduleMeasurement);
    }

    return () => {
      isActive = false;
      window.cancelAnimationFrame(animationFrameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasurement);
    };
  }, [labels, showAll]);

  if (labels.length === 0) return null;

  const renderLabel = (labelItem, isMeasurement = false) => {
    const { date, schedule } = labelItem;
    const status = labelItem.status ?? schedule?.status ?? "in_progress";
    const scheduleId = String(schedule?.id ?? "");
    const scheduleTitle = String(schedule?.title ?? "일정").trim() || "일정";
    const isPublicSchedule = isOrganizationWideSchedule(schedule);
    const scheduleScopeLabel = isPublicSchedule ? "조직 일정" : "개인 일정";
    const scheduleScopeIcon = isPublicSchedule ? (
      <YearPublicStickerIcon />
    ) : (
      <YearPersonalStickerIcon />
    );
    const className = [
      "organizationCalendar__scheduleLabel",
      `is-${status}`,
      !isMeasurement && hoveredScheduleId === scheduleId ? "isHovered" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const key = `${formatDateKey(date)}-${scheduleId}-${labelItem.track}`;

    if (isMeasurement) {
      return (
        <button
          aria-hidden="true"
          className={className}
          data-calendar-measure-label
          key={`measure-${key}`}
          style={resolveScheduleColorStyle(schedule)}
          tabIndex={-1}
          type="button"
        >
          {scheduleScopeIcon}
          <span>{scheduleTitle}</span>
        </button>
      );
    }

    const ariaLabel = `${scheduleScopeLabel} ${scheduleTitle}, ${getStatusLabel(
      status,
    )}, 달성률 ${getScheduleAchievementRate(schedule)}%, ${formatScheduleRange(
      schedule,
    )}`;

    const LabelElement = isReadOnly ? "span" : "button";
    const interactiveProps = isReadOnly
      ? {}
      : {
          onBlur: () => onHoverSchedule(""),
          onClick: (event) => {
            event.stopPropagation();
            onOpenSchedule(schedule);
          },
          onFocus: () => onHoverSchedule(scheduleId),
          onMouseEnter: () => onHoverSchedule(scheduleId),
          onMouseLeave: () => onHoverSchedule(""),
          type: "button",
        };

    return (
      <LabelElement
        aria-label={ariaLabel}
        className={className}
        key={key}
        style={resolveScheduleColorStyle(schedule)}
        title={`${scheduleScopeLabel} · ${scheduleTitle}`}
        {...interactiveProps}
      >
        {scheduleScopeIcon}
        <span>{scheduleTitle}</span>
      </LabelElement>
    );
  };

  const visibleLabelCount = labelLayout.rows.reduce(
    (count, row) =>
      count + row.filter((labelIndex) => labelIndex < labels.length).length,
    0,
  );
  const hiddenCount = labels.length - visibleLabelCount;

  return (
    <span
      className="organizationCalendar__scheduleLabelsViewport"
      ref={viewportRef}
    >
      <span className="organizationCalendar__scheduleLabels">
        {labelLayout.rows.map((row, rowIndex) => (
          <span
            className="organizationCalendar__scheduleLabelRow"
            key={`calendar-label-row-${rowIndex}`}
          >
            {row
              .filter((labelIndex) => labelIndex < labels.length)
              .map((labelIndex) => renderLabel(labels[labelIndex]))}
            {labelLayout.showOverflow &&
              rowIndex === labelLayout.rows.length - 1 &&
              hiddenCount > 0 && (
                <span className="organizationCalendar__scheduleOverflow">
                  +{hiddenCount}
                </span>
              )}
          </span>
        ))}
      </span>
      <span
        aria-hidden="true"
        className="organizationCalendar__scheduleLabels isMeasurement"
        ref={measurementRef}
      >
        {labels.map((labelItem) => renderLabel(labelItem, true))}
        <span
          className="organizationCalendar__scheduleOverflow"
          data-calendar-measure-overflow
        />
      </span>
    </span>
  );
});

export const CalendarWeekScheduleBars = memo(
  function CalendarWeekScheduleBars({
    days,
    hoveredScheduleId = "",
    isLoading = false,
    isReadOnly = false,
    onHoverSchedule = () => {},
    onOpenSchedule = () => {},
    resolveScheduleColorStyle,
    schedules,
  }) {
    const weekBars = useMemo(() => {
      if (!Array.isArray(days) || days.length === 0) return [];

      return sortSchedulesPublicFirst(schedules ?? []).flatMap(
        (schedule, index) => {
          const activeDayIndexes = days.reduce((indexes, day, dayIndex) => {
            if (scheduleOverlapsDay(schedule, day)) indexes.push(dayIndex);
            return indexes;
          }, []);

          if (activeDayIndexes.length === 0) return [];

          const firstDayIndex = activeDayIndexes[0];
          const lastDayIndex =
            activeDayIndexes[activeDayIndexes.length - 1];
          const scheduleStartKey = toDateInputValue(schedule?.startAt);
          const scheduleEndKey = toDateInputValue(schedule?.endAt);
          const weekStartKey = formatDateKey(days[0]);
          const weekEndKey = formatDateKey(days[days.length - 1]);

          return [
            {
              continuesAfterWeek:
                Boolean(scheduleEndKey) && scheduleEndKey > weekEndKey,
              continuesBeforeWeek:
                Boolean(scheduleStartKey) && scheduleStartKey < weekStartKey,
              endColumn: lastDayIndex + 2,
              key: String(
                schedule?.id ??
                  `${schedule?.title}-${schedule?.startAt}-${index}`,
              ),
              schedule,
              startColumn: firstDayIndex + 1,
            },
          ];
        },
      );
    }, [days, schedules]);

    if (isLoading) {
      return (
        <span className="organizationCalendar__weekScheduleBars isLoading">
          <span className="organizationCalendar__loadingLine" />
        </span>
      );
    }

    if (weekBars.length === 0) return null;

    return (
      <span className="organizationCalendar__weekScheduleBars">
        {weekBars.map(
          (
            {
              continuesAfterWeek,
              continuesBeforeWeek,
              endColumn,
              key,
              schedule,
              startColumn,
            },
            rowIndex,
          ) => {
            const scheduleId = String(schedule?.id ?? "");
            const scheduleTitle =
              String(schedule?.title ?? "").trim() || "일정";
            const isPublicSchedule = isOrganizationWideSchedule(schedule);
            const scheduleScopeLabel = isPublicSchedule
              ? "조직 일정"
              : "개인 일정";
            const status =
              schedule?.requestStatus === "rejected"
                ? "interrupted"
                : schedule?.status ?? "in_progress";
            const BarElement = isReadOnly ? "span" : "button";
            const interactiveProps = isReadOnly
              ? {}
              : {
                  onBlur: () => onHoverSchedule(""),
                  onClick: (event) => {
                    event.stopPropagation();
                    onOpenSchedule(schedule);
                  },
                  onFocus: () => onHoverSchedule(scheduleId),
                  onMouseEnter: () => onHoverSchedule(scheduleId),
                  onMouseLeave: () => onHoverSchedule(""),
                  type: "button",
                };

            return (
              <BarElement
                aria-label={`${scheduleScopeLabel} ${scheduleTitle}, ${getStatusLabel(
                  status,
                )}, ${formatScheduleRange(schedule)}`}
                className={[
                  "organizationCalendar__weekScheduleBar",
                  `is-${status}`,
                  continuesBeforeWeek ? "continuesBeforeWeek" : "",
                  continuesAfterWeek ? "continuesAfterWeek" : "",
                  hoveredScheduleId === scheduleId ? "isHovered" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={key}
                style={{
                  ...resolveScheduleColorStyle?.(schedule),
                  gridColumn: `${startColumn} / ${endColumn}`,
                  gridRow: rowIndex + 1,
                }}
                title={`${scheduleScopeLabel} · ${scheduleTitle} · ${formatCompactScheduleRange(
                  schedule,
                )}`}
                {...interactiveProps}
              >
                {isPublicSchedule ? (
                  <YearPublicStickerIcon />
                ) : (
                  <YearPersonalStickerIcon />
                )}
                <span>{scheduleTitle}</span>
              </BarElement>
            );
          },
        )}
      </span>
    );
  },
);

function normalizeSearchText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function normalizeComparableId(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
}

function isSameId(left, right) {
  const normalizedLeft = normalizeComparableId(left);
  const normalizedRight = normalizeComparableId(right);
  return Boolean(
    normalizedLeft && normalizedRight && normalizedLeft === normalizedRight,
  );
}

function getScheduleCreatorId(schedule) {
  return (
    schedule?.createdBy ??
    schedule?.createdById ??
    schedule?.created_by ??
    schedule?.created_by_id ??
    schedule?.creatorId ??
    schedule?.creator?.id ??
    schedule?.createdByUser?.id ??
    ""
  );
}

function getScheduleAssigneeId(schedule) {
  return (
    schedule?.hostUserId ??
    schedule?.host_user_id ??
    schedule?.assigneeUserId ??
    ""
  );
}

function getScheduleResponsibleUserId(schedule) {
  return isTaskRequestSchedule(schedule)
    ? getScheduleAssigneeId(schedule)
    : getScheduleCreatorId(schedule);
}

function isPublicScheduleHeatmapRow(member) {
  return (
    member?.isPublicScheduleRow === true ||
    isSameId(member?.id, PUBLIC_SCHEDULES_HEATMAP_ROW.id)
  );
}

function isScheduleInHeatmapMemberRow(schedule, member) {
  if (!schedule || !member) return false;
  if (isPublicScheduleHeatmapRow(member)) {
    return isOrganizationWideSchedule(schedule);
  }
  if (isOrganizationWideSchedule(schedule)) return false;
  return isSameId(getScheduleResponsibleUserId(schedule), member.id);
}

function scheduleMatchesFilterOption(schedule, option, currentUserId) {
  if (!schedule || !option) return false;

  if (isOrganizationWideSchedule(schedule)) {
    return option.kind === "public_schedules";
  }

  if (option.kind === "my_schedules") {
    return isTaskRequestSchedule(schedule)
      ? isSameId(getScheduleAssigneeId(schedule), currentUserId)
      : isSameId(getScheduleCreatorId(schedule), currentUserId);
  }

  if (option.kind === "my_requests") {
    return (
      isTaskRequestSchedule(schedule) &&
      isSameId(getScheduleCreatorId(schedule), currentUserId)
    );
  }

  if (option.kind === "member") {
    return isSameId(getScheduleResponsibleUserId(schedule), option.memberId);
  }

  return false;
}

function filterSchedulesBySelection(
  schedules,
  selectedIds,
  options,
  currentUserId,
) {
  if (selectedIds == null) return schedules;

  const selectedIdSet = new Set(selectedIds);
  const selectedOptions = options.filter((option) =>
    selectedIdSet.has(option.id),
  );
  if (selectedOptions.length === 0) return [];

  return schedules.filter((schedule) =>
    selectedOptions.some((option) =>
      scheduleMatchesFilterOption(schedule, option, currentUserId),
    ),
  );
}

function filterSchedulesByOrganizationScope(schedules, organizationId) {
  if (!organizationId || organizationId === "all") return schedules;

  return schedules.filter(
    (schedule) =>
      isOrganizationWideSchedule(schedule) ||
      isSameId(schedule?.organizationId, organizationId),
  );
}

function getSchedulePersonName(schedule) {
  if (isOrganizationWideSchedule(schedule)) return "조직 일정";
  return schedule?.hostUserName || schedule?.createdByName || "담당 미지정";
}

function buildMonthCells(anchorDate) {
  const monthStart = startOfMonth(anchorDate);
  const gridStart = startOfWeek(monthStart);
  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
}

function buildMonthDays(anchorDate) {
  const days = [];
  const monthEnd = addMonths(anchorDate, 1);

  for (
    let day = startOfMonth(anchorDate);
    day < monthEnd;
    day = addDays(day, 1)
  ) {
    days.push(day);
  }

  return days;
}

function buildWeekCells(anchorDate) {
  const weekStart = startOfWeek(anchorDate);
  return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
}

function buildYearMonths(anchorDate) {
  const year = anchorDate.getFullYear();
  return Array.from({ length: 12 }, (_, monthIndex) =>
    new Date(year, monthIndex, 1),
  );
}

function buildMiniMonthCells(monthDate) {
  const monthStart = startOfMonth(monthDate);
  const nextMonth = addMonths(monthStart, 1);
  const cells = Array.from({ length: monthStart.getDay() }, () => null);

  for (
    let day = monthStart;
    day < nextMonth;
    day = addDays(day, 1)
  ) {
    cells.push(day);
  }

  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function getRangeForView(anchorDate, viewMode) {
  if (viewMode === "year") {
    const from = new Date(anchorDate.getFullYear(), 0, 1);
    return {
      from,
      to: new Date(anchorDate.getFullYear() + 1, 0, 1),
    };
  }

  if (viewMode === "week") {
    const from = startOfWeek(anchorDate);
    return {
      from,
      to: addDays(from, 7),
    };
  }

  const cells = buildMonthCells(anchorDate);
  return {
    from: cells[0],
    to: addDays(cells[cells.length - 1], 1),
  };
}

function scheduleOverlapsDay(schedule, day) {
  const dateKey = formatDateKey(day);
  const startKey = toDateInputValue(schedule?.startAt);
  const endKey = toDateInputValue(schedule?.endAt);
  const effectiveStartKey = startKey || endKey;
  const effectiveEndKey = endKey || startKey;

  return Boolean(
    effectiveStartKey &&
      effectiveEndKey &&
      effectiveStartKey <= dateKey &&
      effectiveEndKey >= dateKey,
  );
}

function scheduleOverlapsDateRange(schedule, from, to) {
  const fromKey = formatDateKey(from);
  const toKey = formatDateKey(to);
  const startKey = toDateInputValue(schedule?.startAt);
  const endKey = toDateInputValue(schedule?.endAt);
  const effectiveStartKey = startKey || endKey;
  const effectiveEndKey = endKey || startKey;

  return Boolean(
    effectiveStartKey &&
      effectiveEndKey &&
      effectiveStartKey <= toKey &&
      effectiveEndKey >= fromKey,
  );
}

function scheduleRangesOverlap(left, right) {
  const leftStart = toDateInputValue(left?.startAt);
  const leftEnd = toDateInputValue(left?.endAt);
  const rightStart = toDateInputValue(right?.startAt);
  const rightEnd = toDateInputValue(right?.endAt);
  const effectiveLeftStart = leftStart || leftEnd;
  const effectiveLeftEnd = leftEnd || leftStart;
  const effectiveRightStart = rightStart || rightEnd;
  const effectiveRightEnd = rightEnd || rightStart;

  return Boolean(
    effectiveLeftStart &&
    effectiveLeftEnd &&
    effectiveRightStart &&
    effectiveRightEnd &&
    effectiveLeftStart <= effectiveRightEnd &&
    effectiveLeftEnd >= effectiveRightStart,
  );
}

function getPaletteHueDistance(leftPalette, rightPalette) {
  if (leftPalette.hue == null && rightPalette.hue == null) return 0;
  if (leftPalette.hue == null || rightPalette.hue == null) return 72;

  const distance = Math.abs(leftPalette.hue - rightPalette.hue);
  return Math.min(distance, 360 - distance);
}

function chooseSchedulePaletteIndex(
  schedule,
  assignments,
  conflictingAssignments,
) {
  const paletteCount = CALENDAR_LABEL_PALETTES.length;
  const baseIndex =
    hashScheduleColorSeed(getScheduleCalendarColorKey(schedule)) % paletteCount;
  const usageCounts = Array.from({ length: paletteCount }, () => 0);
  const conflictCounts = Array.from({ length: paletteCount }, () => 0);

  assignments.forEach(({ paletteIndex }) => {
    usageCounts[paletteIndex] += 1;
  });
  conflictingAssignments.forEach(({ paletteIndex }) => {
    conflictCounts[paletteIndex] += 1;
  });

  const lowestConflictCount = Math.min(...conflictCounts);
  const candidates = conflictCounts
    .map((count, paletteIndex) => ({ count, paletteIndex }))
    .filter(({ count }) => count === lowestConflictCount);
  const lowestUsageCount = Math.min(
    ...candidates.map(({ paletteIndex }) => usageCounts[paletteIndex]),
  );
  const leastUsedCandidates = candidates.filter(
    ({ paletteIndex }) => usageCounts[paletteIndex] === lowestUsageCount,
  );

  if (conflictingAssignments.length === 0) {
    return leastUsedCandidates.reduce((bestIndex, candidate) => {
      const candidateOffset =
        (candidate.paletteIndex - baseIndex + paletteCount) % paletteCount;
      const bestOffset = (bestIndex - baseIndex + paletteCount) % paletteCount;
      return candidateOffset < bestOffset ? candidate.paletteIndex : bestIndex;
    }, leastUsedCandidates[0].paletteIndex);
  }

  return leastUsedCandidates.reduce((bestIndex, candidate) => {
    const candidatePalette = CALENDAR_LABEL_PALETTES[candidate.paletteIndex];
    const candidateDistance = Math.min(
      ...conflictingAssignments.map(({ paletteIndex }) =>
        getPaletteHueDistance(
          candidatePalette,
          CALENDAR_LABEL_PALETTES[paletteIndex],
        ),
      ),
    );
    const bestPalette = CALENDAR_LABEL_PALETTES[bestIndex];
    const bestDistance = Math.min(
      ...conflictingAssignments.map(({ paletteIndex }) =>
        getPaletteHueDistance(
          bestPalette,
          CALENDAR_LABEL_PALETTES[paletteIndex],
        ),
      ),
    );

    if (candidateDistance !== bestDistance) {
      return candidateDistance > bestDistance
        ? candidate.paletteIndex
        : bestIndex;
    }

    const candidateOffset =
      (candidate.paletteIndex - baseIndex + paletteCount) % paletteCount;
    const bestOffset = (bestIndex - baseIndex + paletteCount) % paletteCount;
    return candidateOffset < bestOffset ? candidate.paletteIndex : bestIndex;
  }, leastUsedCandidates[0].paletteIndex);
}

function buildScheduleCalendarColorStyles(schedules) {
  const colorStyles = new Map();
  const assignments = [];
  const orderedSchedules = [...schedules].sort(
    (left, right) =>
      String(left.createdAt ?? "").localeCompare(
        String(right.createdAt ?? ""),
      ) ||
      getScheduleCalendarColorKey(left).localeCompare(
        getScheduleCalendarColorKey(right),
      ),
  );

  orderedSchedules.forEach((schedule) => {
    const colorKey = getScheduleCalendarColorKey(schedule);
    if (!colorKey || colorStyles.has(colorKey)) return;

    const conflictingAssignments = assignments.filter((assignment) =>
      scheduleRangesOverlap(schedule, assignment.schedule),
    );
    const paletteIndex = chooseSchedulePaletteIndex(
      schedule,
      assignments,
      conflictingAssignments,
    );

    colorStyles.set(
      colorKey,
      getScheduleCalendarPaletteStyle(CALENDAR_LABEL_PALETTES[paletteIndex]),
    );
    assignments.push({ paletteIndex, schedule });
  });

  return colorStyles;
}

function getScheduleDensityLevel(count) {
  if (count <= 0) return 0;
  return Math.min(count, 4);
}

function compareScheduleDates(leftValue, rightValue, direction = "asc") {
  const left = toDateInputValue(leftValue);
  const right = toDateInputValue(rightValue);

  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;

  const compare = left.localeCompare(right);
  return direction === "desc" ? -compare : compare;
}

function sortSchedules(schedules) {
  return [...schedules].sort((left, right) => {
    const dateCompare = compareScheduleDates(
      left.startAt || left.endAt,
      right.startAt || right.endAt,
    );
    const titleCompare = String(left.title ?? "").localeCompare(
      String(right.title ?? ""),
      "ko-KR",
    );

    return dateCompare || titleCompare;
  });
}

function sortSchedulesPublicFirst(schedules) {
  return [...schedules].sort((left, right) => {
    const scopeCompare =
      Number(isOrganizationWideSchedule(right)) -
      Number(isOrganizationWideSchedule(left));
    const dateCompare = compareScheduleDates(
      left.startAt || left.endAt,
      right.startAt || right.endAt,
    );
    const titleCompare = String(left.title ?? "").localeCompare(
      String(right.title ?? ""),
      "ko-KR",
    );

    return scopeCompare || dateCompare || titleCompare;
  });
}

function sortBoardSchedules(schedules) {
  return [...schedules].sort((left, right) => {
    const scopeCompare =
      Number(isOrganizationWideSchedule(right)) -
      Number(isOrganizationWideSchedule(left));
    const priorityCompare =
      getPriorityLevel(right.priority) - getPriorityLevel(left.priority);
    const dateCompare = compareScheduleDates(
      left.startAt || left.endAt,
      right.startAt || right.endAt,
    );
    const titleCompare = String(left.title ?? "").localeCompare(
      String(right.title ?? ""),
      "ko-KR",
    );

    return scopeCompare || priorityCompare || dateCompare || titleCompare;
  });
}

function persistOptionValue(storageKey, value) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(storageKey, value);
  } catch {
    // Ignore storage failures; the UI can still use the in-memory value.
  }
}

function getStoredCalendarViewMode() {
  if (typeof window === "undefined") return "month";

  try {
    const savedValue = window.localStorage.getItem(
      CALENDAR_VIEW_MODE_STORAGE_KEY,
    );
    return CALENDAR_VIEW_MODES.has(savedValue) ? savedValue : "month";
  } catch {
    return "month";
  }
}

function getStoredCalendarDisplayMode() {
  if (typeof window === "undefined") return "calendar";

  try {
    const savedValue = window.localStorage.getItem(
      CALENDAR_DISPLAY_MODE_STORAGE_KEY,
    );
    return CALENDAR_DISPLAY_MODES.has(savedValue) ? savedValue : "calendar";
  } catch {
    return "calendar";
  }
}

function getStoredBooleanValue(storageKey, fallbackValue) {
  if (typeof window === "undefined") return fallbackValue;

  try {
    const savedValue = window.localStorage.getItem(storageKey);
    if (savedValue === "true") return true;
    if (savedValue === "false") return false;
    return fallbackValue;
  } catch {
    return fallbackValue;
  }
}

function getStoredScheduleFilterSelection(storageKey) {
  if (typeof window === "undefined") return null;

  try {
    const savedValue = window.localStorage.getItem(storageKey);
    if (savedValue == null) return null;
    const parsedValue = JSON.parse(savedValue);
    if (!Array.isArray(parsedValue)) return null;

    return Array.from(
      new Set(
        parsedValue.map((value) => String(value ?? "").trim()).filter(Boolean),
      ),
    );
  } catch {
    return null;
  }
}

function persistScheduleFilterSelection(storageKey, selectedIds) {
  if (typeof window === "undefined") return;

  try {
    if (selectedIds == null) {
      window.localStorage.removeItem(storageKey);
      return;
    }
    window.localStorage.setItem(storageKey, JSON.stringify(selectedIds));
  } catch {
    // Ignore storage failures; the UI can still use the in-memory value.
  }
}

function buildCalendarScheduleLabels(visibleDays, schedules) {
  const dayMap = new Map();
  const orderedSchedules = sortSchedulesPublicFirst(schedules);

  visibleDays.forEach((day) => {
    const dateKey = formatDateKey(day);
    const labels = orderedSchedules
      .filter((schedule) => scheduleOverlapsDay(schedule, day))
      .map((schedule, index) => ({
        date: day,
        schedule,
        status: schedule.status ?? "in_progress",
        track: index,
      }));

    dayMap.set(dateKey, { date: day, labels });
  });

  return dayMap;
}

function getDefaultFormForDay(day) {
  const base = startOfDay(day || new Date());

  return {
    ...INITIAL_FORM,
    startAt: formatDateKey(base),
    endAt: formatDateKey(base),
  };
}

function normalizeMembers(members = []) {
  if (!Array.isArray(members)) return [];

  return members
    .map((member) => ({
      id: String(
        member?.id ??
          member?.userId ??
          member?.user_id ??
          member?.memberId ??
          member?.member_id ??
          member?.user?.id ??
          "",
      ).trim(),
      name:
        member?.name ??
        member?.userName ??
        member?.user_name ??
        member?.displayName ??
        member?.display_name ??
        member?.user?.name ??
        "-",
      email:
        member?.email ??
        member?.userEmail ??
        member?.user_email ??
        member?.user?.email ??
        "",
      organizationId:
        member?.organizationId ?? member?.organization_id ?? null,
      organizationName:
        member?.organizationName ?? member?.organization_name ?? "",
      organizationRelation:
        member?.organizationRelation ?? member?.organization_relation ?? "own",
    }))
    .filter((member) => member.id);
}

export default function OrganizationCalendar({
  currentUser,
  organizationId,
  organizationCountryCode,
  organizationCountryName,
  organizationName,
  organizationOptions = [],
  members = [],
  notify,
}) {
  const actorOrganizationId =
    currentUser?.organization_id ?? currentUser?.organizationId ?? null;
  const actorOrganizationName =
    currentUser?.organization_name ??
    currentUser?.organizationName ??
    organizationName ??
    "현재 소속";
  const canCreateSchedule = Boolean(
    actorOrganizationId &&
      organizationId &&
      isSameId(actorOrganizationId, organizationId),
  );
  const [calendarSearchParams, setCalendarSearchParams] = useSearchParams();
  const linkedScheduleId = calendarSearchParams.get("schedule_id") ?? "";
  const linkedScheduleDate = calendarSearchParams.get("schedule_date") ?? "";
  const calendarWorkspaceMode =
    calendarSearchParams.get("calendar_view") === "mine" ? "mine" : "all";
  const [displayMode, setDisplayMode] = useState(
    getStoredCalendarDisplayMode,
  );
  const [viewMode, setViewMode] = useState(() => {
    const storedViewMode = getStoredCalendarViewMode();
    return displayMode === "list" && storedViewMode === "week"
      ? "month"
      : storedViewMode;
  });
  const [anchorDate, setAnchorDate] = useState(
    () => parseDateKey(linkedScheduleDate) ?? new Date(),
  );
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [calendarError, setCalendarError] = useState("");
  const [boardDateRange, setBoardDateRange] = useState(getStoredBoardDateRange);
  const [boardDateDraft, setBoardDateDraft] = useState(() => boardDateRange);
  const [boardSchedules, setBoardSchedules] = useState([]);
  const [isBoardLoading, setIsBoardLoading] = useState(false);
  const [boardError, setBoardError] = useState("");
  const [boardScheduleFilterIds, setBoardScheduleFilterIds] = useState(() =>
    getStoredScheduleFilterSelection(BOARD_SCHEDULE_FILTER_STORAGE_KEY),
  );
  const [viewerScheduleFilterIds, setViewerScheduleFilterIds] = useState(() =>
    getStoredScheduleFilterSelection(VIEWER_SCHEDULE_FILTER_STORAGE_KEY),
  );
  const [boardOrganizationScopeFilterId, setBoardOrganizationScopeFilterId] =
    useState("all");
  const [viewerOrganizationScopeFilterId, setViewerOrganizationScopeFilterId] =
    useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditCancelConfirmOpen, setIsEditCancelConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDashboardCollapsed, setIsDashboardCollapsed] = useState(() =>
    getStoredBooleanValue(DASHBOARD_COLLAPSED_STORAGE_KEY, false),
  );
  const [shouldRenderDashboardControls, setShouldRenderDashboardControls] =
    useState(() => !isDashboardCollapsed);
  const dashboardContentRef = useRef(null);
  const dashboardControlsTimerRef = useRef(null);
  const dashboardOpenFrameRef = useRef(0);
  const boardRequestIdRef = useRef(0);
  const editingScheduleSourceRef = useRef(null);
  const editingScheduleFormSnapshotRef = useRef(null);
  const openedLinkedScheduleIdRef = useRef("");
  const [dashboardContentHeight, setDashboardContentHeight] = useState(0);
  const [scheduleForm, setScheduleForm] = useState(INITIAL_FORM);
  const [selectedDaySummary, setSelectedDaySummary] = useState(null);
  const [selectedDayMemberFilter, setSelectedDayMemberFilter] = useState(null);
  const [selectedScheduleDetail, setSelectedScheduleDetail] = useState(null);
  const [hoveredScheduleId, setHoveredScheduleId] = useState("");
  const [expandedHeatmapMemberIds, setExpandedHeatmapMemberIds] = useState([]);
  const [expandedHeatmapMonthKeys, setExpandedHeatmapMonthKeys] = useState([]);
  const [isTaskRejectionOpen, setIsTaskRejectionOpen] = useState(false);
  const [taskRejectionReason, setTaskRejectionReason] = useState("");
  const [isRespondingToTask, setIsRespondingToTask] = useState(false);
  const [myWorkspaceRefreshKey, setMyWorkspaceRefreshKey] = useState(0);
  const [pendingMyWorkspaceSchedule, setPendingMyWorkspaceSchedule] =
    useState(null);
  const [taskAssignees, setTaskAssignees] = useState([]);

  const memberOptions = useMemo(() => normalizeMembers(members), [members]);
  const organizationScopeOptions = useMemo(() => {
    const currentOrganizationId = normalizeComparableId(organizationId);
    const normalizedRows = Array.isArray(organizationOptions)
      ? organizationOptions
      : [];
    const optionById = new Map();

    normalizedRows.forEach((organization) => {
      const id = normalizeComparableId(
        organization?.organization_id ?? organization?.id,
      );
      if (!id || optionById.has(id)) return;
      optionById.set(id, {
        id,
        label:
          id === currentOrganizationId
            ? `${organization?.name || organizationName || "현재 조직"} 직속`
            : `소속 · ${organization?.name || "소속명 미지정"}`,
      });
    });

    if (currentOrganizationId && !optionById.has(currentOrganizationId)) {
      optionById.set(currentOrganizationId, {
        id: currentOrganizationId,
        label: `${organizationName || "현재 조직"} 직속`,
      });
    }

    return [
      { id: "all", label: "전체 소속" },
      ...optionById.values(),
    ];
  }, [organizationId, organizationName, organizationOptions]);
  const boardOrganizationScopedMemberOptions = useMemo(
    () =>
      boardOrganizationScopeFilterId === "all"
        ? memberOptions
        : memberOptions.filter((member) =>
            isSameId(member.organizationId, boardOrganizationScopeFilterId),
          ),
    [boardOrganizationScopeFilterId, memberOptions],
  );
  const viewerOrganizationScopedMemberOptions = useMemo(
    () =>
      viewerOrganizationScopeFilterId === "all"
        ? memberOptions
        : memberOptions.filter((member) =>
            isSameId(member.organizationId, viewerOrganizationScopeFilterId),
          ),
    [memberOptions, viewerOrganizationScopeFilterId],
  );
  const boardScheduleFilterOptions = useMemo(
    () => [
      {
        id: PUBLIC_SCHEDULES_FILTER_ID,
        kind: "public_schedules",
        label: "조직 일정",
      },
      {
        id: MY_SCHEDULES_FILTER_ID,
        kind: "my_schedules",
        label: "내 일정",
      },
      {
        id: MY_REQUESTS_FILTER_ID,
        kind: "my_requests",
        label: "내가 요청한 일정",
      },
      ...boardOrganizationScopedMemberOptions
        .filter((member) => !isSameId(member.id, currentUser?.id))
        .map((member) => ({
          id: `${MEMBER_FILTER_PREFIX}${member.id}`,
          kind: "member",
          label: member.name,
          memberId: member.id,
        })),
    ],
    [boardOrganizationScopedMemberOptions, currentUser?.id],
  );
  const viewerScheduleFilterOptions = useMemo(
    () => [
      {
        id: PUBLIC_SCHEDULES_FILTER_ID,
        kind: "public_schedules",
        label: "조직 일정",
      },
      {
        id: MY_SCHEDULES_FILTER_ID,
        kind: "my_schedules",
        label: "내 일정",
      },
      {
        id: MY_REQUESTS_FILTER_ID,
        kind: "my_requests",
        label: "내가 요청한 일정",
      },
      ...viewerOrganizationScopedMemberOptions
        .filter((member) => !isSameId(member.id, currentUser?.id))
        .map((member) => ({
          id: `${MEMBER_FILTER_PREFIX}${member.id}`,
          kind: "member",
          label: member.name,
          memberId: member.id,
        })),
    ],
    [currentUser?.id, viewerOrganizationScopedMemberOptions],
  );
  const taskAssigneeOptions = useMemo(
    () =>
      normalizeMembers(taskAssignees).filter(
        (member) =>
          Boolean(scheduleForm.id) || !isSameId(member.id, currentUser?.id),
      ),
    [currentUser?.id, scheduleForm.id, taskAssignees],
  );
  const appliesKoreanHolidays = useMemo(
    () => isKoreanCountry(organizationCountryCode, organizationCountryName),
    [organizationCountryCode, organizationCountryName],
  );
  const anchorYear = anchorDate.getFullYear();
  const activeRangeViewMode =
    displayMode === "calendar" || viewMode === "year" ? viewMode : "month";
  const isYearCalendarView =
    displayMode === "calendar" && viewMode === "year";
  const isYearMemberView = displayMode === "list" && viewMode === "year";
  const isYearRangeView = isYearCalendarView || isYearMemberView;
  const visibleRange = useMemo(
    () => getRangeForView(anchorDate, activeRangeViewMode),
    [activeRangeViewMode, anchorDate],
  );
  const visibleDays = useMemo(
    () => {
      if (viewMode === "year") return [];
      return viewMode === "week"
        ? buildWeekCells(anchorDate)
        : buildMonthCells(anchorDate);
    },
    [anchorDate, viewMode],
  );
  const yearMonths = useMemo(() => buildYearMonths(anchorDate), [anchorDate]);
  const todayKey = formatDateKey(new Date());
  const anchorMonth = anchorDate.getMonth();
  const isEditMode = Boolean(scheduleForm.id);
  const isTaskRequestForm = scheduleForm.scheduleType === "task_request";
  const isOrganizationWideForm =
    scheduleForm.scheduleScope === "organization";
  const canEditForm = !isEditMode || scheduleForm.canEdit;
  const hasEditFormChanges = useMemo(
    () =>
      isEditMode &&
      hasScheduleFormChanges(
        scheduleForm,
        editingScheduleFormSnapshotRef.current,
      ),
    [isEditMode, scheduleForm],
  );

  const visibleSchedules = useMemo(
    () =>
      sortSchedules(
        schedules.filter((schedule) => schedule.requestStatus !== "rejected"),
      ),
    [schedules],
  );
  const viewerOrganizationScopedVisibleSchedules = useMemo(
    () =>
      filterSchedulesByOrganizationScope(
        visibleSchedules,
        viewerOrganizationScopeFilterId,
      ),
    [viewerOrganizationScopeFilterId, visibleSchedules],
  );
  const viewerSchedules = useMemo(
    () =>
      filterSchedulesBySelection(
        viewerOrganizationScopedVisibleSchedules,
        viewerScheduleFilterIds,
        viewerScheduleFilterOptions,
        currentUser?.id,
      ),
    [
      currentUser?.id,
      viewerOrganizationScopedVisibleSchedules,
      viewerScheduleFilterOptions,
      viewerScheduleFilterIds,
    ],
  );
  const yearMonthSummaries = useMemo(() => {
    const now = new Date();

    return yearMonths.map((date) => {
      const monthEnd = addDays(addMonths(date, 1), -1);
      const monthSchedules = sortSchedulesPublicFirst(
        viewerSchedules.filter((schedule) =>
          scheduleOverlapsDateRange(schedule, date, monthEnd),
        ),
      );
      const publicSchedules = monthSchedules.filter(isOrganizationWideSchedule);
      const calendarCells = buildMiniMonthCells(date).map((day, index) => {
        const daySchedules = day
          ? monthSchedules.filter((schedule) =>
              scheduleOverlapsDay(schedule, day),
            )
          : [];

        return {
          date: day,
          isToday: day ? formatDateKey(day) === todayKey : false,
          key: day ? formatDateKey(day) : `empty-${index}`,
          personalScheduleCount: daySchedules.filter(
            (schedule) => !isOrganizationWideSchedule(schedule),
          ).length,
          publicScheduleCount: daySchedules.filter(isOrganizationWideSchedule)
            .length,
        };
      });

      return {
        calendarCells,
        date,
        isCurrentMonth:
          date.getFullYear() === now.getFullYear() &&
          date.getMonth() === now.getMonth(),
        monthKey: formatDateKey(date).slice(0, 7),
        monthLabel: `${date.getMonth() + 1}월`,
        privateScheduleCount:
          monthSchedules.length - publicSchedules.length,
        publicSchedules,
        schedules: monthSchedules,
      };
    });
  }, [todayKey, viewerSchedules, yearMonths]);
  const visibleBoardSchedules = useMemo(
    () =>
      sortSchedules(
        boardSchedules.filter(
          (schedule) => schedule.requestStatus !== "rejected",
        ),
      ),
    [boardSchedules],
  );
  const organizationScopedBoardSchedules = useMemo(
    () =>
      filterSchedulesByOrganizationScope(
        visibleBoardSchedules,
        boardOrganizationScopeFilterId,
      ),
    [boardOrganizationScopeFilterId, visibleBoardSchedules],
  );

  const calendarScheduleColorStyles = useMemo(() => {
    if (displayMode !== "calendar") {
      return new Map();
    }

    if (viewMode === "year") {
      const firstVisibleDay = new Date(anchorYear, 0, 1);
      const lastVisibleDay = new Date(anchorYear, 11, 31);

      return buildScheduleCalendarColorStyles(
        viewerSchedules.filter((schedule) =>
          scheduleOverlapsDateRange(schedule, firstVisibleDay, lastVisibleDay),
        ),
      );
    }

    if (visibleDays.length === 0) return new Map();

    const firstVisibleDay = visibleDays[0];
    const lastVisibleDay = visibleDays[visibleDays.length - 1];
    return buildScheduleCalendarColorStyles(
      viewerSchedules.filter((schedule) =>
        scheduleOverlapsDateRange(schedule, firstVisibleDay, lastVisibleDay),
      ),
    );
  }, [anchorYear, displayMode, viewerSchedules, viewMode, visibleDays]);
  const resolveScheduleCalendarColorStyle = useCallback(
    (schedule) =>
      calendarScheduleColorStyles.get(getScheduleCalendarColorKey(schedule)) ??
      getDefaultScheduleCalendarColorStyle(schedule),
    [calendarScheduleColorStyles],
  );

  useEffect(() => {
    persistOptionValue(
      DASHBOARD_COLLAPSED_STORAGE_KEY,
      String(isDashboardCollapsed),
    );
  }, [isDashboardCollapsed]);

  useEffect(() => {
    persistOptionValue(CALENDAR_VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    persistScheduleFilterSelection(
      BOARD_SCHEDULE_FILTER_STORAGE_KEY,
      boardScheduleFilterIds,
    );
  }, [boardScheduleFilterIds]);

  useEffect(() => {
    persistScheduleFilterSelection(
      VIEWER_SCHEDULE_FILTER_STORAGE_KEY,
      viewerScheduleFilterIds,
    );
  }, [viewerScheduleFilterIds]);

  useEffect(() => {
    setBoardOrganizationScopeFilterId("all");
    setViewerOrganizationScopeFilterId("all");
  }, [organizationId]);

  useEffect(
    () => () => {
      window.clearTimeout(dashboardControlsTimerRef.current);
      window.cancelAnimationFrame(dashboardOpenFrameRef.current);
    },
    [],
  );

  useLayoutEffect(() => {
    if (calendarWorkspaceMode !== "all") return undefined;

    const content = dashboardContentRef.current;
    if (!content) return undefined;

    const updateContentHeight = () => {
      const nextHeight = content.scrollHeight;
      setDashboardContentHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight,
      );
    };

    updateContentHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateContentHeight)
        : null;

    resizeObserver?.observe(content);
    window.addEventListener("resize", updateContentHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateContentHeight);
    };
  }, [
    boardError,
    boardSchedules,
    calendarWorkspaceMode,
    isBoardLoading,
  ]);

  const monthDays = useMemo(() => buildMonthDays(anchorDate), [anchorDate]);

  const memberScheduleHeatmapRows = useMemo(
    () =>
      [
        PUBLIC_SCHEDULES_HEATMAP_ROW,
        ...viewerOrganizationScopedMemberOptions,
      ].map((member) => {
        const monthStart = monthDays[0];
        const monthEnd = monthDays[monthDays.length - 1];
        const memberSchedules = sortSchedulesPublicFirst(
          viewerSchedules.filter(
            (schedule) =>
              isScheduleInHeatmapMemberRow(schedule, member) &&
              (!monthStart ||
                !monthEnd ||
                scheduleOverlapsDateRange(schedule, monthStart, monthEnd)),
          ),
        );
        const cells = monthDays.map((day) => {
          const daySchedules = memberSchedules.filter((schedule) =>
            scheduleOverlapsDay(schedule, day),
          );

          return {
            date: day,
            dateKey: formatDateKey(day),
            level: getScheduleDensityLevel(daySchedules.length),
            schedules: daySchedules,
          };
        });

        return {
          member,
          schedules: memberSchedules,
          cells,
          totalCount: memberSchedules.length,
        };
      }),
    [monthDays, viewerOrganizationScopedMemberOptions, viewerSchedules],
  );

  const yearScheduleHeatmapRows = useMemo(
    () =>
      yearMonths.map((monthStart) => {
        const monthEnd = addDays(addMonths(monthStart, 1), -1);
        const monthSchedules = sortSchedulesPublicFirst(
          viewerSchedules.filter((schedule) =>
            scheduleOverlapsDateRange(schedule, monthStart, monthEnd),
          ),
        );
        const daysInMonth = monthEnd.getDate();
        const cells = YEAR_HEATMAP_DAY_NUMBERS.map((dayNumber) => {
          if (dayNumber > daysInMonth) {
            return {
              date: null,
              dateKey: "",
              dayNumber,
              level: 0,
              schedules: [],
            };
          }

          const date = new Date(
            monthStart.getFullYear(),
            monthStart.getMonth(),
            dayNumber,
          );
          const daySchedules = monthSchedules.filter((schedule) =>
            scheduleOverlapsDay(schedule, date),
          );

          return {
            date,
            dateKey: formatDateKey(date),
            dayNumber,
            level: getScheduleDensityLevel(daySchedules.length),
            schedules: daySchedules,
          };
        });

        return {
          cells,
          monthKey: formatDateKey(monthStart).slice(0, 7),
          monthLabel: `${monthStart.getMonth() + 1}월`,
          schedules: monthSchedules,
          totalCount: monthSchedules.length,
        };
      }),
    [viewerSchedules, yearMonths],
  );

  const hasExpandedHeatmapMembers =
    displayMode === "list" &&
    viewMode !== "year" &&
    expandedHeatmapMemberIds.some((memberId) =>
      isSameId(memberId, PUBLIC_SCHEDULES_HEATMAP_ROW.id) ||
      viewerOrganizationScopedMemberOptions.some((member) =>
        isSameId(member.id, memberId),
      ),
    );
  const hasExpandedHeatmapMonths =
    isYearMemberView &&
    expandedHeatmapMonthKeys.some((monthKey) =>
      yearScheduleHeatmapRows.some((row) => row.monthKey === monthKey),
    );
  const hasExpandedHeatmapRows =
    hasExpandedHeatmapMembers || hasExpandedHeatmapMonths;
  const shouldShowDensityLegend =
    displayMode === "list" && !hasExpandedHeatmapRows;
  const shouldShowStatusLegend = hasExpandedHeatmapRows;

  const statusBoardSchedules = useMemo(
    () =>
      filterSchedulesBySelection(
        organizationScopedBoardSchedules,
        boardScheduleFilterIds,
        boardScheduleFilterOptions,
        currentUser?.id,
      ),
    [
      boardScheduleFilterIds,
      boardScheduleFilterOptions,
      currentUser?.id,
      organizationScopedBoardSchedules,
    ],
  );

  const boardStatusColumns = useMemo(
    () =>
      STATUS_OPTIONS.map((status) => ({
        ...status,
        schedules: sortBoardSchedules(
          statusBoardSchedules.filter(
            (schedule) => (schedule.status ?? "in_progress") === status.value,
          ),
        ),
      })),
    [statusBoardSchedules],
  );

  const loadSchedules = useCallback(async () => {
    if (!organizationId) {
      setSchedules([]);
      setCalendarError("조직 정보를 확인할 수 없습니다.");
      return;
    }

    setIsLoading(true);
    setCalendarError("");

    const result = await getOrganizationCalendarSchedules({
      organizationId,
      from: visibleRange.from.toISOString(),
      to: visibleRange.to.toISOString(),
    });

    setIsLoading(false);

    if (!result.success) {
      setSchedules([]);
      setCalendarError(result.message || "조직 일정을 불러오지 못했습니다.");
      return;
    }

    setSchedules(
      Array.isArray(result.data?.schedules) ? result.data.schedules : [],
    );
  }, [organizationId, visibleRange.from, visibleRange.to]);

  const loadBoardSchedules = useCallback(async () => {
    const requestId = boardRequestIdRef.current + 1;
    boardRequestIdRef.current = requestId;

    if (!organizationId) {
      setIsBoardLoading(false);
      setBoardSchedules([]);
      setBoardError("조직 정보를 확인할 수 없습니다.");
      return;
    }

    const fromDate = parseDateKey(boardDateRange.from);
    const toDate = parseDateKey(boardDateRange.to);
    if (!fromDate || !toDate || fromDate > toDate) {
      setIsBoardLoading(false);
      setBoardSchedules([]);
      setBoardError("상태별 일정 조회 기간을 확인해 주세요.");
      return;
    }

    setIsBoardLoading(true);
    setBoardError("");

    const result = await getOrganizationCalendarSchedules({
      organizationId,
      from: fromDate.toISOString(),
      to: addDays(toDate, 1).toISOString(),
    });

    if (requestId !== boardRequestIdRef.current) return;
    setIsBoardLoading(false);

    if (!result.success) {
      setBoardSchedules([]);
      setBoardError(result.message || "상태별 일정을 불러오지 못했습니다.");
      return;
    }

    setBoardSchedules(
      Array.isArray(result.data?.schedules) ? result.data.schedules : [],
    );
  }, [boardDateRange.from, boardDateRange.to, organizationId]);

  useEffect(() => {
    let cancelled = false;

    if (!currentUser?.id || !organizationId) {
      setTaskAssignees([]);
      return undefined;
    }

    (async () => {
      const result = await getOrganizationCalendarTaskAssignees();
      if (cancelled) return;

      setTaskAssignees(
        result.success && Array.isArray(result.data?.assignees)
          ? result.data.assignees
          : [],
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, organizationId]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  useEffect(() => {
    if (!linkedScheduleId || !linkedScheduleDate) return;

    const nextAnchorDate = parseDateKey(linkedScheduleDate);
    if (!nextAnchorDate) return;

    setDisplayMode("calendar");
    setAnchorDate((current) =>
      current.getFullYear() === nextAnchorDate.getFullYear() &&
      current.getMonth() === nextAnchorDate.getMonth()
        ? current
        : nextAnchorDate,
    );
  }, [linkedScheduleDate, linkedScheduleId]);

  useEffect(() => {
    if (!linkedScheduleId) {
      openedLinkedScheduleIdRef.current = "";
      return;
    }

    if (isLoading || openedLinkedScheduleIdRef.current === linkedScheduleId) {
      return;
    }

    const linkedSchedule = schedules.find((schedule) =>
      isSameId(schedule.id, linkedScheduleId),
    );
    if (!linkedSchedule) return;

    openedLinkedScheduleIdRef.current = linkedScheduleId;
    setSelectedDaySummary(null);
    setSelectedDayMemberFilter(null);
    setSelectedScheduleDetail(linkedSchedule);
  }, [isLoading, linkedScheduleId, schedules]);

  useEffect(() => {
    persistBoardDateRange(boardDateRange);
    if (calendarWorkspaceMode !== "all") return;

    loadBoardSchedules();
  }, [boardDateRange, calendarWorkspaceMode, loadBoardSchedules]);

  const handleBoardDateDraftChange = useCallback((field, value) => {
    setBoardDateDraft((current) => ({ ...current, [field]: value }));
    setBoardError("");
  }, []);

  const handleApplyBoardDateRange = useCallback(
    (event) => {
      event.preventDefault();

      const draftFrom = parseDateKey(boardDateDraft.from);
      const draftTo = parseDateKey(boardDateDraft.to);

      if (!draftFrom || !draftTo) {
        setBoardError("시작일과 종료일을 모두 선택해 주세요.");
        return;
      }

      if (draftFrom > draftTo) {
        setBoardError("시작일은 종료일보다 늦을 수 없습니다.");
        return;
      }

      const nextRange = {
        from: boardDateDraft.from,
        to: boardDateDraft.to,
      };
      const isSameRange =
        nextRange.from === boardDateRange.from &&
        nextRange.to === boardDateRange.to;

      setBoardError("");
      if (isSameRange) {
        loadBoardSchedules();
        return;
      }

      setBoardDateRange(nextRange);
    },
    [boardDateDraft, boardDateRange, loadBoardSchedules],
  );

  const schedulesByDate = useMemo(() => {
    const map = new Map();

    visibleDays.forEach((day) => {
      map.set(
        formatDateKey(day),
        sortSchedules(
          viewerSchedules.filter((schedule) =>
            scheduleOverlapsDay(schedule, day),
          ),
        ),
      );
    });

    return map;
  }, [viewerSchedules, visibleDays]);

  const calendarLabelsByDate = useMemo(
    () => buildCalendarScheduleLabels(visibleDays, viewerSchedules),
    [viewerSchedules, visibleDays],
  );

  const hoveredSchedule = useMemo(
    () =>
      hoveredScheduleId
        ? viewerSchedules.find(
            (schedule) => String(schedule.id) === hoveredScheduleId,
          )
        : null,
    [hoveredScheduleId, viewerSchedules],
  );

  const hoveredScheduleColorStyle = useMemo(
    () =>
      hoveredSchedule
        ? resolveScheduleCalendarColorStyle(hoveredSchedule)
        : undefined,
    [hoveredSchedule, resolveScheduleCalendarColorStyle],
  );

  const selectedDaySchedules = useMemo(
    () =>
      selectedDaySummary
        ? sortSchedules(
            viewerSchedules.filter(
              (schedule) =>
                scheduleOverlapsDay(schedule, selectedDaySummary) &&
                (!selectedDayMemberFilter?.id ||
                  isScheduleInHeatmapMemberRow(
                    schedule,
                    selectedDayMemberFilter,
                  )),
            ),
          )
        : [],
    [selectedDayMemberFilter, selectedDaySummary, viewerSchedules],
  );

  const handleNotify = useCallback(
    (message, success = true) => {
      if (notify) {
        notify(message, success);
      }
    },
    [notify],
  );

  const handleMoveCalendar = useCallback(
    (direction) => {
      setAnchorDate((current) => {
        if (viewMode === "year") {
          return addYears(current, direction);
        }
        if (displayMode === "calendar" && viewMode === "week") {
          return addDays(current, direction * 7);
        }
        return addMonths(current, direction);
      });
    },
    [displayMode, viewMode],
  );

  const handleMoveCalendarYear = useCallback((direction) => {
    setAnchorDate((current) => addYears(current, direction));
  }, []);

  const handleSelectDisplayMode = useCallback((nextDisplayMode) => {
    if (!CALENDAR_DISPLAY_MODES.has(nextDisplayMode)) return;

    setDisplayMode(nextDisplayMode);
    persistOptionValue(CALENDAR_DISPLAY_MODE_STORAGE_KEY, nextDisplayMode);
    if (nextDisplayMode === "list") {
      setViewMode((current) => (current === "week" ? "month" : current));
    }
  }, []);

  const handleSelectCalendarWorkspaceMode = useCallback(
    (nextMode) => {
      if (nextMode !== "all" && nextMode !== "mine") return;

      const nextSearchParams = new URLSearchParams(calendarSearchParams);
      if (nextMode === "mine") {
        nextSearchParams.set("calendar_view", "mine");
      } else {
        nextSearchParams.delete("calendar_view");
      }
      nextSearchParams.delete("schedule_id");
      nextSearchParams.delete("schedule_date");

      setSelectedDaySummary(null);
      setSelectedDayMemberFilter(null);
      setSelectedScheduleDetail(null);
      setCalendarSearchParams(nextSearchParams, { replace: true });
    },
    [calendarSearchParams, setCalendarSearchParams],
  );

  const handleToggleDashboard = useCallback(() => {
    window.clearTimeout(dashboardControlsTimerRef.current);
    window.cancelAnimationFrame(dashboardOpenFrameRef.current);

    if (isDashboardCollapsed) {
      setShouldRenderDashboardControls(true);
      dashboardOpenFrameRef.current = window.requestAnimationFrame(() => {
        setIsDashboardCollapsed(false);
      });
      return;
    }

    setIsDashboardCollapsed(true);
    dashboardControlsTimerRef.current = window.setTimeout(() => {
      setShouldRenderDashboardControls(false);
    }, DASHBOARD_TRANSITION_MS);
  }, [isDashboardCollapsed]);

  const handleToggleHeatmapMember = useCallback((memberId) => {
    setExpandedHeatmapMemberIds((current) => {
      const normalizedMemberId = String(memberId ?? "").trim();
      if (!normalizedMemberId) return current;

      return current.some((expandedMemberId) =>
        isSameId(expandedMemberId, normalizedMemberId),
      )
        ? current.filter(
            (expandedMemberId) =>
              !isSameId(expandedMemberId, normalizedMemberId),
          )
        : [...current, normalizedMemberId];
    });
  }, []);

  const handleToggleHeatmapMonth = useCallback((monthKey) => {
    setExpandedHeatmapMonthKeys((current) =>
      current.includes(monthKey)
        ? current.filter((expandedMonthKey) => expandedMonthKey !== monthKey)
        : [...current, monthKey],
    );
  }, []);

  const handleOpenCreate = useCallback(
    (day = new Date()) => {
      if (!canCreateSchedule) {
        handleNotify(
          "내 소속 페이지에서만 일정을 등록할 수 있습니다.",
          false,
        );
        return;
      }

      editingScheduleSourceRef.current = null;
      editingScheduleFormSnapshotRef.current = null;
      setIsEditCancelConfirmOpen(false);
      setSelectedDaySummary(null);
      setSelectedDayMemberFilter(null);
      setSelectedScheduleDetail(null);
      setIsTaskRejectionOpen(false);
      setTaskRejectionReason("");
      setScheduleForm(getDefaultFormForDay(day));
      setIsModalOpen(true);
    },
    [canCreateSchedule, handleNotify],
  );

  const handleOpenEdit = useCallback((schedule) => {
    const editForm = getEditFormForSchedule(schedule);

    editingScheduleSourceRef.current = schedule;
    editingScheduleFormSnapshotRef.current = editForm;
    setIsEditCancelConfirmOpen(false);
    setSelectedDaySummary(null);
    setSelectedDayMemberFilter(null);
    setSelectedScheduleDetail(null);
    setIsTaskRejectionOpen(false);
    setTaskRejectionReason("");
    setScheduleForm(editForm);
    setIsModalOpen(true);
  }, []);

  const handleOpenScheduleDetail = useCallback((schedule) => {
    setIsTaskRejectionOpen(false);
    setTaskRejectionReason("");
    setSelectedScheduleDetail(schedule);
  }, []);

  const handleOpenDaySummary = useCallback((day, member = null) => {
    setSelectedScheduleDetail(null);
    setSelectedDayMemberFilter(member);
    setSelectedDaySummary(day);
  }, []);

  const handleDayKeyDown = useCallback((event, day) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    setSelectedDayMemberFilter(null);
    setSelectedDaySummary(day);
  }, []);

  const handleCloseDaySummary = useCallback(() => {
    setSelectedDaySummary(null);
    setSelectedDayMemberFilter(null);
  }, []);

  const handleCloseScheduleDetail = useCallback(() => {
    if (isRespondingToTask) return;
    setSelectedScheduleDetail(null);
    setSelectedDaySummary(null);
    setSelectedDayMemberFilter(null);

    if (calendarSearchParams.has("schedule_id")) {
      const nextSearchParams = new URLSearchParams(calendarSearchParams);
      nextSearchParams.delete("schedule_id");
      nextSearchParams.delete("schedule_date");
      setCalendarSearchParams(nextSearchParams, { replace: true });
    }
    setIsTaskRejectionOpen(false);
    setTaskRejectionReason("");
  }, [calendarSearchParams, isRespondingToTask, setCalendarSearchParams]);

  const handleReturnToDaySummary = useCallback(() => {
    if (isRespondingToTask || !selectedDaySummary) return;

    setSelectedScheduleDetail(null);
    setIsTaskRejectionOpen(false);
    setTaskRejectionReason("");

    if (calendarSearchParams.has("schedule_id")) {
      const nextSearchParams = new URLSearchParams(calendarSearchParams);
      nextSearchParams.delete("schedule_id");
      nextSearchParams.delete("schedule_date");
      setCalendarSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    calendarSearchParams,
    isRespondingToTask,
    selectedDaySummary,
    setCalendarSearchParams,
  ]);

  const handleExitEdit = useCallback(() => {
    const sourceSchedule = editingScheduleSourceRef.current;

    editingScheduleSourceRef.current = null;
    editingScheduleFormSnapshotRef.current = null;
    setIsEditCancelConfirmOpen(false);
    setIsModalOpen(false);
    setScheduleForm(INITIAL_FORM);
    setSelectedScheduleDetail(sourceSchedule);
  }, []);

  const handleCloseModal = useCallback(() => {
    if (isSaving || isDeleting) return;

    if (isEditMode) {
      if (hasEditFormChanges) {
        setIsEditCancelConfirmOpen(true);
      } else {
        handleExitEdit();
      }
      return;
    }

    setIsModalOpen(false);
    setScheduleForm(INITIAL_FORM);
  }, [handleExitEdit, hasEditFormChanges, isDeleting, isEditMode, isSaving]);

  const handleCloseEditCancelConfirm = useCallback(() => {
    setIsEditCancelConfirmOpen(false);
  }, []);

  const handleConfirmCancelEdit = useCallback(() => {
    handleExitEdit();
  }, [handleExitEdit]);

  const handleFormChange = useCallback((field, value) => {
    setScheduleForm((current) => {
      const shouldAlignEndDate =
        field === "startAt" &&
        Boolean(value) &&
        Boolean(current.endAt) &&
        current.endAt < value;

      return {
        ...current,
        [field]: value,
        ...(shouldAlignEndDate ? { endAt: value } : {}),
        ...(field === "scheduleType"
          ? {
              hostUserId: "",
              ...(value === "task_request" ? { scheduleScope: "personal" } : {}),
            }
          : {}),
      };
    });
  }, []);

  const handleAchievementRateChange = useCallback(
    (value) => {
      handleFormChange(
        "achievementRate",
        String(normalizeAchievementRate(value)),
      );
    },
    [handleFormChange],
  );

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!canEditForm) {
        handleNotify("이 일정을 수정할 권한이 없습니다.", false);
        return;
      }

      const title = scheduleForm.title.trim();
      if (!title) {
        handleNotify("일정 제목을 입력해 주세요.", false);
        return;
      }

      if (
        scheduleForm.startAt &&
        scheduleForm.endAt &&
        scheduleForm.endAt < scheduleForm.startAt
      ) {
        handleNotify("마감일은 시작일보다 빠를 수 없습니다.", false);
        return;
      }

      if (isTaskRequestForm && !scheduleForm.hostUserId) {
        handleNotify("업무를 요청할 구성원을 선택해 주세요.", false);
        return;
      }

      setIsSaving(true);
      const payload = {
        organizationId: actorOrganizationId || organizationId,
        title,
        description: scheduleForm.description.trim(),
        scheduleType: scheduleForm.scheduleType,
        scheduleScope: scheduleForm.scheduleScope,
        status:
          !isEditMode && isTaskRequestForm ? "pending" : scheduleForm.status,
        achievementRate:
          !isEditMode && isTaskRequestForm
            ? 0
            : normalizeAchievementRate(scheduleForm.achievementRate),
        priority: normalizePriorityValue(scheduleForm.priority),
        hostUserId: isTaskRequestForm ? scheduleForm.hostUserId || null : null,
        startAt: scheduleForm.startAt || null,
        endAt: scheduleForm.endAt || null,
      };
      const result = isEditMode
        ? await updateOrganizationCalendarSchedule(scheduleForm.id, payload)
        : await createOrganizationCalendarSchedule(payload);
      setIsSaving(false);

      if (!result.success) {
        handleNotify(result.message || "일정을 저장하지 못했습니다.", false);
        return;
      }

      handleNotify(result.message || "일정이 저장되었습니다.");
      if (!isEditMode && isTaskRequestForm) {
        requestGlobalNotificationsRefresh();
      }
      if (!isEditMode && result.data?.scheduleId) {
        const hostMember = taskAssigneeOptions.find((member) =>
          isSameId(member.id, payload.hostUserId),
        );
        const now = new Date().toISOString();

        setPendingMyWorkspaceSchedule({
          achievementRate: payload.achievementRate,
          canDelete: true,
          canEdit: true,
          canRespond: false,
          commentCount: 0,
          createdAt: now,
          createdBy: currentUser?.id ?? "",
          createdByName: currentUser?.user_name ?? "",
          description: payload.description,
          endAt: payload.endAt,
          hostUserId: payload.hostUserId,
          hostUserName: hostMember?.name ?? "",
          id: result.data.scheduleId,
          latestComment: null,
          organizationId:
            result.data?.organizationId ||
            actorOrganizationId ||
            organizationId,
          organizationName: actorOrganizationName,
          priority: payload.priority,
          rejectionReason: "",
          requestStatus:
            payload.scheduleType === "task_request"
              ? "pending"
              : "not_required",
          respondedAt: null,
          scheduleScope: payload.scheduleScope,
          scheduleType: payload.scheduleType,
          startAt: payload.startAt,
          status:
            payload.scheduleType === "task_request"
              ? "pending"
              : payload.status,
          title: payload.title,
          updatedAt: now,
        });
      }
      editingScheduleSourceRef.current = null;
      editingScheduleFormSnapshotRef.current = null;
      setIsEditCancelConfirmOpen(false);
      setIsModalOpen(false);
      setScheduleForm(INITIAL_FORM);
      setMyWorkspaceRefreshKey((current) => current + 1);
      await Promise.all([loadSchedules(), loadBoardSchedules()]);
    },
    [
      canEditForm,
      actorOrganizationId,
      actorOrganizationName,
      handleNotify,
      isEditMode,
      isTaskRequestForm,
      loadBoardSchedules,
      loadSchedules,
      currentUser?.id,
      currentUser?.user_name,
      organizationId,
      scheduleForm,
      taskAssigneeOptions,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (!scheduleForm.id || !scheduleForm.canDelete) {
      handleNotify("이 일정을 삭제할 권한이 없습니다.", false);
      return;
    }

    setIsDeleting(true);
    const result = await deleteOrganizationCalendarSchedule(scheduleForm.id, {
      organizationId,
    });
    setIsDeleting(false);

    if (!result.success) {
      handleNotify(result.message || "일정을 삭제하지 못했습니다.", false);
      return;
    }

    handleNotify(result.message || "일정이 삭제되었습니다.");
    editingScheduleSourceRef.current = null;
    editingScheduleFormSnapshotRef.current = null;
    setIsEditCancelConfirmOpen(false);
    setIsModalOpen(false);
    setScheduleForm(INITIAL_FORM);
    setMyWorkspaceRefreshKey((current) => current + 1);
    await Promise.all([loadSchedules(), loadBoardSchedules()]);
  }, [
    handleNotify,
    loadBoardSchedules,
    loadSchedules,
    organizationId,
    scheduleForm,
  ]);

  const handleTaskRequestResponse = useCallback(
    async (action) => {
      if (!selectedScheduleDetail?.id || !selectedScheduleDetail.canRespond) {
        handleNotify("처리할 수 있는 업무 요청이 아닙니다.", false);
        return;
      }

      const rejectionReason = taskRejectionReason.trim();
      if (action === "reject" && !rejectionReason) {
        handleNotify("반려 이유를 입력해 주세요.", false);
        return;
      }

      setIsRespondingToTask(true);
      const result = await respondOrganizationCalendarTaskRequest(
        selectedScheduleDetail.id,
        {
          action,
          rejectionReason,
        },
      );
      setIsRespondingToTask(false);

      if (!result.success) {
        handleNotify(
          result.message || "업무 요청을 처리하지 못했습니다.",
          false,
        );
        return;
      }

      const requestStatus =
        result.data?.requestStatus ??
        (action === "accept" ? "accepted" : "rejected");
      setSelectedScheduleDetail((current) =>
        current
          ? {
              ...current,
              requestStatus,
              rejectionReason:
                result.data?.rejectionReason ??
                (action === "reject" ? rejectionReason : ""),
              status: action === "accept" ? "in_progress" : current.status,
              canRespond: false,
              canEdit: action === "accept" ? true : current.canEdit,
            }
          : current,
      );
      setIsTaskRejectionOpen(false);
      setTaskRejectionReason("");
      handleNotify(result.message || "업무 요청을 처리했습니다.");
      requestGlobalNotificationsRefresh();
      setMyWorkspaceRefreshKey((current) => current + 1);
      await Promise.all([loadSchedules(), loadBoardSchedules()]);
    },
    [
      handleNotify,
      loadBoardSchedules,
      loadSchedules,
      selectedScheduleDetail,
      taskRejectionReason,
    ],
  );

  const renderScheduleLabels = (dayLabels = { labels: [] }) => {
    return (
      <CalendarScheduleLabels
        dayLabels={dayLabels}
        hoveredScheduleId={hoveredScheduleId}
        onHoverSchedule={setHoveredScheduleId}
        onOpenSchedule={handleOpenScheduleDetail}
        resolveScheduleColorStyle={resolveScheduleCalendarColorStyle}
      />
    );
  };

  const handleMyWorkspaceDataChanged = useCallback(() => {
    void Promise.all([loadSchedules(), loadBoardSchedules()]);
  }, [loadBoardSchedules, loadSchedules]);

  return (
    <>
      <section className="organizationCalendar__workspaceSwitchShell">
        <div
          aria-label="일정 페이지 선택"
          className="organizationCalendar__workspaceSwitch"
          role="tablist"
        >
          <button
            aria-selected={calendarWorkspaceMode === "all"}
            className={calendarWorkspaceMode === "all" ? "isActive" : ""}
            onClick={() => handleSelectCalendarWorkspaceMode("all")}
            role="tab"
            type="button"
          >
            <CalendarDays aria-hidden="true" size={16} />
            전체 일정
          </button>
          <button
            aria-selected={calendarWorkspaceMode === "mine"}
            className={calendarWorkspaceMode === "mine" ? "isActive" : ""}
            onClick={() => handleSelectCalendarWorkspaceMode("mine")}
            role="tab"
            type="button"
          >
            <UserRound aria-hidden="true" size={16} />
            내 일정 관리
          </button>
        </div>
      </section>

      {calendarWorkspaceMode === "mine" && (
        <OrganizationMySchedules
          canCreateSchedule={canCreateSchedule}
          currentUser={currentUser}
          notify={notify}
          onCreateSchedule={handleOpenCreate}
          onDataChanged={handleMyWorkspaceDataChanged}
          onOpenSchedule={handleOpenScheduleDetail}
          onPendingScheduleApplied={(scheduleId) => {
            setPendingMyWorkspaceSchedule((current) =>
              isSameId(current?.id, scheduleId) ? null : current,
            );
          }}
          organizationId={organizationId}
          organizationName={organizationName}
          pendingSchedule={pendingMyWorkspaceSchedule}
          refreshKey={myWorkspaceRefreshKey}
        />
      )}

      {calendarWorkspaceMode === "all" && (
        <section
        className="organizationPage__panel organizationPage__panel--full organizationCalendar__dashboardShell"
        data-state={isDashboardCollapsed ? "closed" : "open"}
        >
        <div className="organizationCalendar__dashboardToggleBar">
          {shouldRenderDashboardControls && (
            <form
              className="organizationCalendar__boardToolbar"
              onSubmit={handleApplyBoardDateRange}
            >
              <strong>조회 기간</strong>
              <label>
                <input
                  aria-label="시작일"
                  max={boardDateDraft.to || undefined}
                  onChange={(event) =>
                    handleBoardDateDraftChange("from", event.target.value)
                  }
                  type="date"
                  value={boardDateDraft.from}
                />
              </label>
              <span className="organizationCalendar__boardToolbarSeparator">
                ~
              </span>
              <label>
                <input
                  aria-label="종료일"
                  min={boardDateDraft.from || undefined}
                  onChange={(event) =>
                    handleBoardDateDraftChange("to", event.target.value)
                  }
                  type="date"
                  value={boardDateDraft.to}
                />
              </label>
              <button disabled={isBoardLoading} type="submit">
                조회
              </button>
              <CalendarScheduleFilterDropdown
                idPrefix="organization-calendar-board"
                onChange={setBoardScheduleFilterIds}
                options={boardScheduleFilterOptions}
                selectedIds={boardScheduleFilterIds}
              />
              <CalendarOrganizationScopeSelect
                onChange={setBoardOrganizationScopeFilterId}
                options={organizationScopeOptions}
                value={boardOrganizationScopeFilterId}
              />
            </form>
          )}

          <div className="organizationCalendar__dashboardToggleActions">
            <button
              className="organizationPage__createBtn"
              disabled={!canCreateSchedule}
              onClick={() => handleOpenCreate(new Date())}
              title={
                canCreateSchedule
                  ? "일정 등록"
                  : "내 소속 페이지에서만 일정을 등록할 수 있습니다."
              }
              type="button"
            >
              <Plus size={15} aria-hidden="true" />
              <span>일정 등록</span>
            </button>
            <button
              aria-controls="organization-calendar-status-board"
              aria-expanded={!isDashboardCollapsed}
              className="organizationCalendar__dashboardToggle"
              onClick={handleToggleDashboard}
              type="button"
            >
              <ChevronDown
                className="organizationCalendar__dashboardToggleIcon"
                size={15}
                aria-hidden="true"
              />
              <span>
                {isDashboardCollapsed
                  ? "상태별 일정 펼치기"
                  : "상태별 일정 접기"}
              </span>
            </button>
          </div>
        </div>

        <div
          aria-label="상태별 일정"
          aria-hidden={isDashboardCollapsed}
          className="organizationCalendar__dashboardBlind"
          id="organization-calendar-status-board"
          role="region"
          style={{
            "--dashboard-blind-height": `${dashboardContentHeight}px`,
          }}
        >
          <div
            className="organizationCalendar__dashboardBlindInner"
            ref={dashboardContentRef}
          >
            {boardError && (
              <p className="organizationCalendar__boardError" role="alert">
                {boardError}
              </p>
            )}
            <OrganizationCalendarBoard
              columns={boardStatusColumns}
              isLoading={isBoardLoading}
              onOpenSchedule={handleOpenScheduleDetail}
            />
          </div>
        </div>
        </section>
      )}

      <article
        className={[
          "organizationPage__panel",
          "organizationPage__panel--full",
          "organizationCalendar",
          calendarWorkspaceMode === "mine"
            ? "organizationCalendar--modalHost"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {calendarWorkspaceMode === "all" && (
          <>
        <div className="organizationCalendar__toolbar">
          <div className="organizationCalendar__toolbarControls">
            <div className="organizationCalendar__modeSwitch" role="tablist">
              <button
                aria-label="일정표 보기"
                aria-selected={displayMode === "calendar"}
                className={displayMode === "calendar" ? "isActive" : ""}
                onClick={() => handleSelectDisplayMode("calendar")}
                role="tab"
                title="일정표 보기"
                type="button"
              >
                <CalendarDays size={16} aria-hidden="true" />
              </button>
              <button
                aria-label="구성원별 보기"
                aria-selected={displayMode === "list"}
                className={displayMode === "list" ? "isActive" : ""}
                onClick={() => handleSelectDisplayMode("list")}
                role="tab"
                title="구성원별 보기"
                type="button"
              >
                <List size={16} aria-hidden="true" />
              </button>
            </div>

            {(displayMode === "calendar" || displayMode === "list") && (
              <div className="organizationCalendar__viewSwitch" role="tablist">
                <button
                  aria-selected={viewMode === "year"}
                  className={viewMode === "year" ? "isActive" : ""}
                  onClick={() => setViewMode("year")}
                  role="tab"
                  type="button"
                >
                  년
                </button>
                <button
                  aria-selected={viewMode === "month"}
                  className={viewMode === "month" ? "isActive" : ""}
                  onClick={() => setViewMode("month")}
                  role="tab"
                  type="button"
                >
                  월
                </button>
                {displayMode === "calendar" && (
                  <button
                    aria-selected={viewMode === "week"}
                    className={viewMode === "week" ? "isActive" : ""}
                    onClick={() => setViewMode("week")}
                    role="tab"
                    type="button"
                  >
                    주
                  </button>
                )}
              </div>
            )}

            <CalendarScheduleFilterDropdown
              idPrefix="organization-calendar-viewer"
              onChange={setViewerScheduleFilterIds}
              options={viewerScheduleFilterOptions}
              selectedIds={viewerScheduleFilterIds}
            />
            <CalendarOrganizationScopeSelect
              onChange={setViewerOrganizationScopeFilterId}
              options={organizationScopeOptions}
              value={viewerOrganizationScopeFilterId}
            />

            {shouldShowDensityLegend && (
              <div
                aria-label="일정 집중도 범례"
                className="organizationCalendar__densityLegend"
              >
                {DENSITY_LEGEND_OPTIONS.map((option) => (
                  <span
                    className={`organizationCalendar__densityLegendItem isLevel-${option.level}`}
                    key={option.level}
                  >
                    <i aria-hidden="true" />
                    <span>{option.label}</span>
                  </span>
                ))}
              </div>
            )}

            {shouldShowStatusLegend && (
              <div
                aria-label="일정 상태 범례"
                className="organizationCalendar__statusLegend"
              >
                {STATUS_OPTIONS.map((status) => (
                  <span
                    className={`organizationCalendar__statusLegendItem is-${status.value}`}
                    key={status.value}
                  >
                    <i aria-hidden="true" />
                    <span>{status.label}</span>
                  </span>
                ))}
              </div>
            )}

            {displayMode === "calendar" &&
              (viewMode === "year" || viewMode === "month") && (
              <div
                aria-label="달력 일정 유형 아이콘 범례"
                className="organizationCalendar__yearStickerLegend"
              >
                <span>
                  <YearPublicStickerIcon />
                  <span>조직 일정</span>
                </span>
                <span>
                  <YearPersonalStickerIcon />
                  <span>개인 일정</span>
                </span>
              </div>
              )}

            <div className="organizationCalendar__navigator">
              {!isYearRangeView && (
                <button
                  aria-label="1년 전"
                  className="isYearMove"
                  onClick={() => handleMoveCalendarYear(-1)}
                  title="1년 전"
                  type="button"
                >
                  <ChevronsLeft size={17} aria-hidden="true" />
                </button>
              )}
              <button
                aria-label={isYearRangeView ? "이전 연도" : "이전"}
                onClick={() => handleMoveCalendar(-1)}
                type="button"
              >
                <ChevronLeft size={17} aria-hidden="true" />
              </button>
              <span
                aria-label={
                  isYearRangeView ? "현재 표시 연도" : "현재 표시 월"
                }
                className="organizationCalendar__navigatorCurrent"
              >
                {isYearRangeView
                  ? `${anchorYear}년`
                  : formatYearMonthLabel(anchorDate)}
              </span>
              <button
                aria-label={isYearRangeView ? "다음 연도" : "다음"}
                onClick={() => handleMoveCalendar(1)}
                type="button"
              >
                <ChevronRight size={17} aria-hidden="true" />
              </button>
              {!isYearRangeView && (
                <button
                  aria-label="1년 후"
                  className="isYearMove"
                  onClick={() => handleMoveCalendarYear(1)}
                  title="1년 후"
                  type="button"
                >
                  <ChevronsRight size={17} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>

        {calendarError && (
          <p className="organizationPage__panelNote isError">{calendarError}</p>
        )}

        {displayMode === "calendar" ? (
          viewMode === "year" ? (
            <OrganizationCalendarYearView
              canCreateSchedule={canCreateSchedule}
              isLoading={isLoading}
              months={yearMonthSummaries}
              onOpenCreate={handleOpenCreate}
              onOpenSchedule={handleOpenScheduleDetail}
            />
          ) : (
            <div
              className={`organizationCalendar__grid ${
                viewMode === "week" ? "isWeek" : "isMonth"
              }`}
            >
            {WEEKDAY_LABELS.map((weekday, index) => (
              <div
                className={[
                  "organizationCalendar__weekday",
                  index === 0 ? "isSunday" : "",
                  index === 6 ? "isSaturday" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={weekday}
              >
                {weekday}
              </div>
            ))}

            {visibleDays.map((day) => {
              const dateKey = formatDateKey(day);
              const dayOfWeek = day.getDay();
              const daySchedules = schedulesByDate.get(dateKey) ?? [];
              const dayLabels = calendarLabelsByDate.get(dateKey);
              const holidayName = appliesKoreanHolidays
                ? getKoreanHolidayName(day)
                : "";
              const registrarNames = getScheduleRegistrarNames(daySchedules);
              const isHoveredScheduleDay =
                viewMode !== "week" &&
                hoveredSchedule &&
                scheduleOverlapsDay(hoveredSchedule, day);

              return (
                <div
                  className={[
                    "organizationCalendar__day",
                    dayOfWeek === 0 ? "isSunday" : "",
                    dayOfWeek === 6 ? "isSaturday" : "",
                    holidayName ? "isHoliday" : "",
                    dateKey === todayKey ? "isToday" : "",
                    isHoveredScheduleDay ? "isScheduleHighlighted" : "",
                    day.getMonth() !== anchorMonth && viewMode === "month"
                      ? "isMuted"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  key={dateKey}
                  onClick={(event) => {
                    if (
                      event.target.closest(
                        ".organizationCalendar__peopleHover",
                      )
                    ) {
                      return;
                    }
                    handleOpenDaySummary(day);
                  }}
                  onKeyDown={(event) => handleDayKeyDown(event, day)}
                  role="button"
                  style={
                    viewMode === "week"
                      ? {
                          ...(isHoveredScheduleDay
                            ? hoveredScheduleColorStyle
                            : {}),
                          gridColumn: dayOfWeek + 1,
                          gridRow: 2,
                        }
                      : isHoveredScheduleDay
                        ? hoveredScheduleColorStyle
                        : undefined
                  }
                  tabIndex={0}
                >
                  <span className="organizationCalendar__dayHeader">
                    <span className="organizationCalendar__dayDate">
                      <span>{day.getDate()}</span>
                      {holidayName && (
                        <small title={holidayName}>{holidayName}</small>
                      )}
                    </span>
                    <span className="organizationCalendar__dayHeaderActions">
                      {dateKey === todayKey && <small>오늘</small>}
                      {!isLoading && daySchedules.length > 0 && (
                        <span
                          aria-label={`${dateKey} 일정 등록자`}
                          className="organizationCalendar__peopleHover"
                        >
                          <UserRound size={15} aria-hidden="true" />
                          <span>{registrarNames.length}</span>
                          <span
                            className="organizationCalendar__peopleTooltip"
                            id={`organization-calendar-registrars-${dateKey}`}
                            role="tooltip"
                          >
                            {registrarNames.map((name) => (
                              <span key={name}>{name}</span>
                            ))}
                          </span>
                        </span>
                      )}
                      <button
                        aria-label={`${dateKey} 일정 등록`}
                        className="organizationCalendar__addDayButton"
                        disabled={!canCreateSchedule}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenCreate(day);
                        }}
                        onKeyDown={(event) => event.stopPropagation()}
                        title={
                          canCreateSchedule
                            ? `${dateKey} 일정 등록`
                            : "내 소속 페이지에서만 일정을 등록할 수 있습니다."
                        }
                        type="button"
                      >
                        <Plus size={15} aria-hidden="true" />
                      </button>
                    </span>
                  </span>
                  {viewMode !== "week" && (
                    <span className="organizationCalendar__daySchedules">
                      {isLoading ? (
                        <span className="organizationCalendar__loadingLine" />
                      ) : daySchedules.length > 0 ? (
                        renderScheduleLabels(dayLabels)
                      ) : null}
                    </span>
                  )}
                </div>
              );
            })}

            {viewMode === "week" && (
              <CalendarWeekScheduleBars
                days={visibleDays}
                hoveredScheduleId={hoveredScheduleId}
                isLoading={isLoading}
                onHoverSchedule={setHoveredScheduleId}
                onOpenSchedule={handleOpenScheduleDetail}
                resolveScheduleColorStyle={resolveScheduleCalendarColorStyle}
                schedules={viewerSchedules}
              />
            )}
            </div>
          )
        ) : (
          <div className="organizationCalendar__agenda" aria-busy={isLoading}>
            {isLoading ? (
              <div className="organizationCalendar__agendaLoading">
                <span className="organizationCalendar__loadingLine" />
                <span className="organizationCalendar__loadingLine" />
                <span className="organizationCalendar__loadingLine" />
              </div>
            ) : isYearMemberView ? (
              <OrganizationCalendarYearHeatmap
                expandedMonthKeys={expandedHeatmapMonthKeys}
                onOpenDay={(day) => handleOpenDaySummary(day)}
                onOpenSchedule={handleOpenScheduleDetail}
                onToggleMonth={handleToggleHeatmapMonth}
                rows={yearScheduleHeatmapRows}
                todayKey={todayKey}
              />
            ) : memberScheduleHeatmapRows.length > 0 ? (
              <div className="organizationCalendar__heatmapWrap">
                <table className="organizationCalendar__heatmap">
                  <thead>
                    <tr>
                      <th
                        className="organizationCalendar__heatmapMemberHeader"
                        scope="col"
                      >
                        구성원
                      </th>
                      {monthDays.map((day) => {
                        const dateKey = formatDateKey(day);

                        return (
                          <th
                            className={[
                              "organizationCalendar__heatmapDateHeader",
                              day.getDay() === 0 ? "isSunday" : "",
                              day.getDay() === 6 ? "isSaturday" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            key={dateKey}
                            scope="col"
                          >
                            <span>{day.getDate()}</span>
                            <small>{WEEKDAY_LABELS[day.getDay()]}</small>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {memberScheduleHeatmapRows.map((row) => {
                      const isExpanded = expandedHeatmapMemberIds.some(
                        (memberId) => isSameId(memberId, row.member.id),
                      );
                      const isPublicScheduleRow =
                        isPublicScheduleHeatmapRow(row.member);

                      return (
                        <Fragment key={row.member.id}>
                          <tr
                            className={[
                              "organizationCalendar__heatmapMemberRow",
                              isExpanded ? "isExpanded" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            <th
                              className={[
                                "organizationCalendar__heatmapMember",
                                isExpanded ? "isExpanded" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              scope="row"
                            >
                              <div className="organizationCalendar__heatmapMemberSummary">
                                <button
                                  aria-expanded={isExpanded}
                                  className="organizationCalendar__heatmapMemberName"
                                  onClick={() =>
                                    handleToggleHeatmapMember(row.member.id)
                                  }
                                  type="button"
                                >
                                  <strong>{row.member.name}</strong>
                                </button>
                                <span className="organizationCalendar__heatmapMemberCount">
                                  {row.totalCount}건
                                </span>
                              </div>
                            </th>
                            {row.cells.map((cell) => {
                              const count = cell.schedules.length;
                              const isTodayCell = cell.dateKey === todayKey;
                              const titles = cell.schedules
                                .map((schedule) => schedule.title)
                                .filter(Boolean)
                                .join(", ");
                              const label = isPublicScheduleRow
                                ? `${formatScheduleDate(
                                    cell.date,
                                  )} 조직 일정 ${count}건`
                                : `${row.member.name} ${formatScheduleDate(
                                    cell.date,
                                  )} 일정 ${count}건`;

                              return (
                                <td
                                  className={[
                                    "organizationCalendar__heatmapCell",
                                    `isLevel-${cell.level}`,
                                    isTodayCell ? "isToday" : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  key={`${row.member.id}-${cell.dateKey}`}
                                >
                                  <button
                                    aria-label={label}
                                    onClick={() =>
                                      handleOpenDaySummary(
                                        cell.date,
                                        row.member,
                                      )
                                    }
                                    title={titles || label}
                                    type="button"
                                  >
                                    <span>{count > 0 ? count : ""}</span>
                                  </button>
                                </td>
                              );
                            })}
                          </tr>

                          {isExpanded && row.schedules.length === 0 ? (
                            <tr className="organizationCalendar__heatmapScheduleRow isEmpty">
                              <th
                                className="organizationCalendar__heatmapSchedule isEmpty"
                                scope="row"
                              >
                                <span>일정 없음</span>
                              </th>
                              <td
                                className="organizationCalendar__heatmapScheduleEmpty"
                                colSpan={monthDays.length}
                              >
                                선택한 달에 일정이 없습니다.
                              </td>
                            </tr>
                          ) : null}

                          {isExpanded
                            ? row.schedules.map((schedule) => {
                                const status = schedule.status ?? "in_progress";
                                const scheduleId = String(
                                  schedule.id ??
                                    `${row.member.id}-${schedule.title}-${schedule.startAt}`,
                                );
                                const scheduleTitle =
                                  String(schedule.title ?? "").trim() || "일정";

                                return (
                                  <tr
                                    className={[
                                      "organizationCalendar__heatmapScheduleRow",
                                      `is-${status}`,
                                    ].join(" ")}
                                    key={`${row.member.id}-${scheduleId}`}
                                  >
                                    <th
                                      className="organizationCalendar__heatmapSchedule"
                                      scope="row"
                                    >
                                      <button
                                        className="organizationCalendar__heatmapScheduleSummary"
                                        onClick={() =>
                                          handleOpenScheduleDetail(schedule)
                                        }
                                        type="button"
                                      >
                                        <HeatmapScheduleTitle
                                          schedule={schedule}
                                          title={scheduleTitle}
                                        />
                                      </button>
                                    </th>
                                    {monthDays.map((day) => {
                                      const dateKey = formatDateKey(day);
                                      const isActive = scheduleOverlapsDay(
                                        schedule,
                                        day,
                                      );
                                      const isTodayCell = dateKey === todayKey;
                                      const label = isActive
                                        ? `${row.member.name} ${scheduleTitle} ${formatScheduleDate(
                                            day,
                                          )} ${getStatusLabel(status)}`
                                        : `${row.member.name} ${scheduleTitle} ${formatScheduleDate(
                                            day,
                                          )} 일정 없음`;

                                      return (
                                        <td
                                          className={[
                                            "organizationCalendar__heatmapScheduleCell",
                                            isTodayCell ? "isToday" : "",
                                            isActive ? `is-${status}` : "",
                                          ]
                                            .filter(Boolean)
                                            .join(" ")}
                                          key={`${scheduleId}-${dateKey}`}
                                        >
                                          <button
                                            aria-label={label}
                                            disabled={!isActive}
                                            onClick={() =>
                                              handleOpenScheduleDetail(schedule)
                                            }
                                            title={
                                              isActive
                                                ? `${scheduleTitle} · ${getStatusLabel(
                                                    status,
                                                  )}`
                                                : ""
                                            }
                                            type="button"
                                          >
                                            <span>
                                              {isActive
                                                ? getStatusLabel(status)
                                                : ""}
                                            </span>
                                          </button>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })
                            : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="organizationPage__emptyState isCompact">
                <CalendarDays size={28} aria-hidden="true" />
                <strong>표시할 구성원이 없습니다.</strong>
              </div>
            )}
          </div>
        )}

          </>
        )}

        {selectedDaySummary && !selectedScheduleDetail && (
          <div
            className="organizationPage__modalBackdrop"
            onMouseDown={handleCloseDaySummary}
            role="presentation"
          >
            <section
              aria-labelledby="organization-calendar-day-title"
              aria-modal="true"
              className="organizationPage__modal organizationCalendar__modal"
              onMouseDown={(event) => event.stopPropagation()}
              role="dialog"
            >
              <div className="organizationPage__modalHeader">
                <div>
                  <h3 id="organization-calendar-day-title">
                    {formatScheduleDate(selectedDaySummary)}{" "}
                    {selectedDayMemberFilter
                      ? isPublicScheduleHeatmapRow(selectedDayMemberFilter)
                        ? "조직 일정"
                        : `${selectedDayMemberFilter.name} 일정`
                      : "일정 요약"}
                  </h3>
                </div>
                <div className="organizationCalendar__daySummaryHeaderActions">
                  {selectedDaySchedules.length > 0 && (
                    <button
                      className="organizationPage__createBtn organizationCalendar__daySummaryCreateButton"
                      disabled={!canCreateSchedule}
                      onClick={() => handleOpenCreate(selectedDaySummary)}
                      title={
                        canCreateSchedule
                          ? "일정 등록"
                          : "내 소속 페이지에서만 일정을 등록할 수 있습니다."
                      }
                      type="button"
                    >
                      <Plus size={15} aria-hidden="true" />
                      일정 등록
                    </button>
                  )}
                  <button
                    aria-label="닫기"
                    className="organizationPage__modalClose"
                    onClick={handleCloseDaySummary}
                    type="button"
                  >
                    <X size={17} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="organizationPage__modalBody">
                {selectedDaySchedules.length > 0 ? (
                  <div className="organizationCalendar__dayDetailList">
                    {selectedDaySchedules.map((schedule) => (
                      <button
                        className={[
                          "organizationCalendar__dayDetailItem",
                          `is-${schedule.status}`,
                        ].join(" ")}
                        key={schedule.id}
                        onClick={() => handleOpenScheduleDetail(schedule)}
                        type="button"
                      >
                        <span className="organizationCalendar__dayDetailOwner">
                          {isOrganizationWideSchedule(schedule) ? (
                            <Building2
                              aria-hidden="true"
                              color={SCHEDULE_SCOPE_ICON_COLOR}
                              size={16}
                            />
                          ) : (
                            <UserRound
                              aria-hidden="true"
                              color={SCHEDULE_SCOPE_ICON_COLOR}
                              size={16}
                            />
                          )}
                          <strong>{getSchedulePersonName(schedule)}</strong>
                        </span>
                        <span className="organizationCalendar__dayDetailBody">
                          <strong>{schedule.title}</strong>
                          <PriorityBadge value={schedule.priority} />
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="organizationPage__emptyState isCompact">
                    <button
                      aria-label={`${formatScheduleDate(selectedDaySummary)} 새 일정 등록하기`}
                      className="organizationCalendar__dayDetailCreateButton"
                      disabled={!canCreateSchedule}
                      onClick={() => handleOpenCreate(selectedDaySummary)}
                      title={
                        canCreateSchedule
                          ? "새 일정 등록하기"
                          : "내 소속 페이지에서만 일정을 등록할 수 있습니다."
                      }
                      type="button"
                    >
                      <Plus size={17} aria-hidden="true" />
                      <span>새 일정 등록하기</span>
                    </button>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {selectedScheduleDetail && (
          <div
            className="organizationPage__modalBackdrop"
            onMouseDown={handleCloseScheduleDetail}
            role="presentation"
          >
            <section
              aria-labelledby="organization-calendar-schedule-title"
              aria-modal="true"
              className="organizationPage__modal organizationCalendar__modal organizationCalendar__modal--scheduleDetail"
              onMouseDown={(event) => event.stopPropagation()}
              role="dialog"
            >
              <div className="organizationPage__modalHeader">
                <div className="organizationCalendar__scheduleDetailHeaderTitle">
                  {selectedDaySummary && (
                    <button
                      aria-label={`${formatScheduleDate(selectedDaySummary)} 일정 목록으로 돌아가기`}
                      className="organizationCalendar__scheduleDetailBackButton"
                      onClick={handleReturnToDaySummary}
                      type="button"
                    >
                      <ChevronLeft aria-hidden="true" size={16} />
                      <span>목록으로 돌아가기</span>
                    </button>
                  )}
                  <h3 id="organization-calendar-schedule-title">일정 상세</h3>
                </div>
                <button
                  aria-label="닫기"
                  className="organizationPage__modalClose"
                  onClick={handleCloseScheduleDetail}
                  type="button"
                >
                  <X size={17} aria-hidden="true" />
                </button>
              </div>

              <div className="organizationPage__modalBody organizationCalendar__detailView">
                <div className="organizationCalendar__detailHero">
                  <strong>{selectedScheduleDetail.title}</strong>
                  <span className="organizationCalendar__detailBadges">
                    <ScheduleScopeBadge schedule={selectedScheduleDetail} />
                    {isTaskRequestSchedule(selectedScheduleDetail) && (
                      <span
                        className={`organizationCalendar__requestStatusBadge is-${selectedScheduleDetail.requestStatus}`}
                      >
                        {getTaskRequestStatusLabel(
                          selectedScheduleDetail.requestStatus,
                        )}
                      </span>
                    )}
                    <span
                      className={`organizationCalendar__statusBadge is-${selectedScheduleDetail.status}`}
                    >
                      {getStatusLabel(selectedScheduleDetail.status)}
                    </span>
                  </span>
                </div>

                <dl className="organizationCalendar__readonlyGrid">
                  <div>
                    <dt>등록 소속</dt>
                    <dd>
                      {selectedScheduleDetail.organizationName || "-"}
                    </dd>
                  </div>
                  <div>
                    <dt>적용 대상</dt>
                    <dd>{getScheduleScopeLabel(selectedScheduleDetail)}</dd>
                  </div>
                  <div>
                    <dt>
                      {isTaskRequestSchedule(selectedScheduleDetail)
                        ? "요청한 사람"
                        : "등록자"}
                    </dt>
                    <dd>{getScheduleRegistrarName(selectedScheduleDetail)}</dd>
                  </div>
                  {isTaskRequestSchedule(selectedScheduleDetail) && (
                    <div>
                      <dt>요청받은 사람</dt>
                      <dd>{getScheduleOwnerName(selectedScheduleDetail)}</dd>
                    </div>
                  )}
                  <div>
                    <dt>중요도</dt>
                    <dd>
                      <PriorityBadge value={selectedScheduleDetail.priority} />
                    </dd>
                  </div>
                  <div>
                    <dt>달성률</dt>
                    <dd>
                      <AchievementMeter
                        value={getScheduleAchievementRate(
                          selectedScheduleDetail,
                        )}
                      />
                    </dd>
                  </div>
                  <div>
                    <dt>기간</dt>
                    <dd>{formatScheduleRange(selectedScheduleDetail)}</dd>
                  </div>
                </dl>

                <section className="organizationCalendar__readonlyDescription">
                  <strong>내용</strong>
                  <p>
                    {String(selectedScheduleDetail.description ?? "").trim() ||
                      "등록된 내용이 없습니다."}
                  </p>
                </section>

                <OrganizationScheduleHistory
                  defaultOpen
                  hideWhenEmpty
                  key={`history-${selectedScheduleDetail.id}`}
                  organizationId={organizationId}
                  schedule={selectedScheduleDetail}
                  showAuthor
                />

                {isTaskRequestSchedule(selectedScheduleDetail) &&
                  selectedScheduleDetail.requestStatus === "rejected" && (
                    <section className="organizationCalendar__rejectionReason">
                      <strong>반려 이유</strong>
                      <p>
                        {selectedScheduleDetail.rejectionReason ||
                          "등록된 반려 이유가 없습니다."}
                      </p>
                    </section>
                  )}

                {selectedScheduleDetail.canRespond && (
                  <section className="organizationCalendar__taskResponsePanel">
                    <div>
                      <strong>업무 요청을 확인해 주세요.</strong>
                      <p>
                        수락하면 일정이 진행 중으로 변경됩니다. 반려 시에는
                        요청자에게 전달할 이유가 필요합니다.
                      </p>
                    </div>
                    {isTaskRejectionOpen ? (
                      <div className="organizationCalendar__taskRejectForm">
                        <label htmlFor="organization-calendar-rejection-reason">
                          반려 이유
                        </label>
                        <textarea
                          autoFocus
                          disabled={isRespondingToTask}
                          id="organization-calendar-rejection-reason"
                          maxLength={500}
                          onChange={(event) =>
                            setTaskRejectionReason(event.target.value)
                          }
                          placeholder="요청자에게 전달할 반려 이유를 입력하세요."
                          value={taskRejectionReason}
                        />
                        <div>
                          <button
                            className="organizationPage__secondaryBtn"
                            disabled={isRespondingToTask}
                            onClick={() => {
                              setIsTaskRejectionOpen(false);
                              setTaskRejectionReason("");
                            }}
                            type="button"
                          >
                            취소
                          </button>
                          <button
                            className="organizationPage__dangerBtn"
                            disabled={isRespondingToTask}
                            onClick={() =>
                              void handleTaskRequestResponse("reject")
                            }
                            type="button"
                          >
                            <XCircle aria-hidden="true" size={15} />
                            {isRespondingToTask
                              ? "처리 중"
                              : "반려 사유 보내기"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="organizationCalendar__taskResponseActions">
                        <button
                          className="organizationPage__saveBtn"
                          disabled={isRespondingToTask}
                          onClick={() =>
                            void handleTaskRequestResponse("accept")
                          }
                          type="button"
                        >
                          <CheckCircle2 aria-hidden="true" size={15} />
                          {isRespondingToTask ? "처리 중" : "수락"}
                        </button>
                        <button
                          className="organizationPage__dangerBtn"
                          disabled={isRespondingToTask}
                          onClick={() => setIsTaskRejectionOpen(true)}
                          type="button"
                        >
                          <XCircle aria-hidden="true" size={15} />
                          반려
                        </button>
                      </div>
                    )}
                  </section>
                )}

                <OrganizationScheduleComments
                  key={selectedScheduleDetail.id}
                  notify={handleNotify}
                  organizationId={organizationId}
                  scheduleId={selectedScheduleDetail.id}
                />
              </div>

              <div className="organizationPage__modalActions">
                {selectedScheduleDetail.canEdit && (
                  <button
                    className="organizationPage__saveBtn"
                    onClick={() => handleOpenEdit(selectedScheduleDetail)}
                    type="button"
                  >
                    <CalendarPlus size={15} aria-hidden="true" />
                    <span>수정</span>
                  </button>
                )}
              </div>
            </section>
          </div>
        )}

        {isModalOpen && (
          <div
            className="organizationPage__modalBackdrop"
            onMouseDown={handleCloseModal}
            role="presentation"
          >
            <form
              aria-labelledby="organization-calendar-modal-title"
              aria-modal="true"
              className={[
                "organizationPage__modal",
                "organizationCalendar__modal",
                isEditMode ? "organizationCalendar__modal--scheduleEdit" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onMouseDown={(event) => event.stopPropagation()}
              onSubmit={handleSubmit}
              role="dialog"
            >
              <div className="organizationPage__modalHeader">
                <div>
                  <h3 id="organization-calendar-modal-title">
                    {isEditMode
                      ? isTaskRequestForm
                        ? "업무 요청 수정"
                        : "일정 수정"
                      : isTaskRequestForm
                        ? "업무 요청"
                        : "일정 등록"}
                  </h3>
                  {!isEditMode && (
                    <span>{actorOrganizationName} 업무일정</span>
                  )}
                </div>
                <button
                  aria-label="닫기"
                  className="organizationPage__modalClose"
                  onClick={handleCloseModal}
                  type="button"
                >
                  <X size={17} aria-hidden="true" />
                </button>
              </div>

              <div className="organizationPage__modalBody organizationCalendar__form">
                <label className="organizationPage__formRow">
                  <span>제목</span>
                  <input
                    disabled={!canEditForm}
                    maxLength={160}
                    onChange={(event) =>
                      handleFormChange("title", event.target.value)
                    }
                    placeholder="업무일정 제목"
                    type="text"
                    value={scheduleForm.title}
                  />
                </label>

                <div className="organizationPage__formRow organizationCalendar__taskTypeRow">
                  <span id="organization-calendar-task-type-label">
                    등록 유형
                  </span>
                  {isEditMode ? (
                    <strong className="organizationCalendar__taskTypeReadonly">
                      {isTaskRequestForm ? "업무 요청" : "직접 등록"}
                    </strong>
                  ) : (
                    <span
                      aria-labelledby="organization-calendar-task-type-label"
                      className="organizationCalendar__taskTypePicker"
                      role="radiogroup"
                    >
                      <button
                        aria-checked={!isTaskRequestForm}
                        className={!isTaskRequestForm ? "isActive" : ""}
                        onClick={() =>
                          handleFormChange("scheduleType", "personal")
                        }
                        role="radio"
                        type="button"
                      >
                        <CalendarDays aria-hidden="true" size={15} />직접 등록
                      </button>
                      <button
                        aria-checked={isTaskRequestForm}
                        className={isTaskRequestForm ? "isActive" : ""}
                        onClick={() =>
                          handleFormChange("scheduleType", "task_request")
                        }
                        role="radio"
                        type="button"
                      >
                        <Send aria-hidden="true" size={15} />
                        다른 사람에게 요청
                      </button>
                    </span>
                  )}
                </div>

                <div className="organizationPage__formRow organizationCalendar__scopeRow">
                  <span id="organization-calendar-scope-label">적용 대상</span>
                  {isTaskRequestForm ? (
                    <strong className="organizationCalendar__taskTypeReadonly">
                      개인 일정
                    </strong>
                  ) : (
                    <span
                      aria-labelledby="organization-calendar-scope-label"
                      className="organizationCalendar__taskTypePicker organizationCalendar__scopePicker"
                      role="radiogroup"
                    >
                      <button
                        aria-checked={!isOrganizationWideForm}
                        className={!isOrganizationWideForm ? "isActive" : ""}
                        disabled={!canEditForm}
                        onClick={() =>
                          handleFormChange("scheduleScope", "personal")
                        }
                        role="radio"
                        type="button"
                      >
                        <UserRound
                          aria-hidden="true"
                          color={SCHEDULE_SCOPE_ICON_COLOR}
                          size={15}
                        />
                        개인 일정
                      </button>
                      <button
                        aria-checked={isOrganizationWideForm}
                        className={isOrganizationWideForm ? "isActive" : ""}
                        disabled={!canEditForm}
                        onClick={() =>
                          handleFormChange("scheduleScope", "organization")
                        }
                        role="radio"
                        type="button"
                      >
                        <Building2
                          aria-hidden="true"
                          color={SCHEDULE_SCOPE_ICON_COLOR}
                          size={15}
                        />
                        조직 일정
                      </button>
                    </span>
                  )}
                </div>

                <div className="organizationCalendar__formGrid">
                  <div className="organizationPage__formRow">
                    <span>시작일</span>
                    <span className="organizationCalendar__dateFieldControl">
                      <input
                        aria-label="시작일"
                        disabled={!canEditForm || !scheduleForm.startAt}
                        onChange={(event) =>
                          handleFormChange("startAt", event.target.value)
                        }
                        type="date"
                        value={scheduleForm.startAt}
                      />
                      <label>
                        <input
                          checked={!scheduleForm.startAt}
                          disabled={!canEditForm}
                          onChange={(event) =>
                            handleFormChange(
                              "startAt",
                              event.target.checked
                                ? ""
                                : scheduleForm.endAt || formatDateKey(new Date()),
                            )
                          }
                          type="checkbox"
                        />
                        미정
                      </label>
                    </span>
                  </div>
                  <div className="organizationPage__formRow">
                    <span>마감일</span>
                    <span className="organizationCalendar__dateFieldControl">
                      <input
                        aria-label="마감일"
                        disabled={!canEditForm || !scheduleForm.endAt}
                        min={scheduleForm.startAt || undefined}
                        onChange={(event) =>
                          handleFormChange("endAt", event.target.value)
                        }
                        type="date"
                        value={scheduleForm.endAt}
                      />
                      <label>
                        <input
                          checked={!scheduleForm.endAt}
                          disabled={!canEditForm}
                          onChange={(event) =>
                            handleFormChange(
                              "endAt",
                              event.target.checked
                                ? ""
                                : scheduleForm.startAt || formatDateKey(new Date()),
                            )
                          }
                          type="checkbox"
                        />
                        미정
                      </label>
                    </span>
                  </div>
                </div>

                {isTaskRequestForm && !isEditMode && (
                  <p className="organizationCalendar__taskRequestGuide">
                    요청을 받은 사용자가 수락하면 진행 중인 일정으로 변경됩니다.
                    반려하면 입력한 사유가 요청자 알림에 표시됩니다.
                  </p>
                )}

                {(!isTaskRequestForm ||
                  (isEditMode &&
                    scheduleForm.requestStatus === "accepted")) && (
                  <div className="organizationCalendar__formGrid">
                    <label className="organizationPage__formRow">
                      <span>상태</span>
                      <select
                        disabled={!canEditForm}
                        onChange={(event) =>
                          handleFormChange("status", event.target.value)
                        }
                        value={scheduleForm.status}
                      >
                        {STATUS_OPTIONS.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="organizationPage__formRow">
                      <span>달성률</span>
                      <span className="organizationCalendar__achievementControl">
                        <input
                          disabled={!canEditForm}
                          max="100"
                          min="0"
                          onChange={(event) =>
                            handleAchievementRateChange(event.target.value)
                          }
                          step="1"
                          type="range"
                          value={normalizeAchievementRate(
                            scheduleForm.achievementRate,
                          )}
                        />
                        <input
                          disabled={!canEditForm}
                          max="100"
                          min="0"
                          onChange={(event) =>
                            handleAchievementRateChange(event.target.value)
                          }
                          step="1"
                          type="number"
                          value={scheduleForm.achievementRate}
                        />
                        <strong>
                          {formatAchievementRate(scheduleForm.achievementRate)}
                        </strong>
                      </span>
                    </label>
                  </div>
                )}

                <div className="organizationCalendar__formGrid">
                  <div className="organizationPage__formRow">
                    <span id="organization-calendar-priority-label">
                      중요도
                    </span>
                    <span
                      aria-labelledby="organization-calendar-priority-label"
                      className="organizationCalendar__priorityPicker"
                      role="radiogroup"
                    >
                      {PRIORITY_OPTIONS.map((priority) => {
                        const currentLevel = getPriorityLevel(
                          scheduleForm.priority,
                        );
                        const isSelected = currentLevel === priority.level;
                        const isFilled = priority.level <= currentLevel;

                        return (
                          <button
                            aria-checked={isSelected}
                            aria-label={priority.label}
                            className={[
                              "organizationCalendar__priorityStarButton",
                              isFilled ? "isFilled" : "",
                              isSelected ? "isSelected" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            disabled={!canEditForm}
                            key={priority.value}
                            onClick={() =>
                              handleFormChange("priority", priority.value)
                            }
                            role="radio"
                            title={priority.label}
                            type="button"
                          >
                            <Star aria-hidden="true" fill="currentColor" />
                          </button>
                        );
                      })}
                    </span>
                  </div>
                </div>

                {isTaskRequestForm && (
                  <label className="organizationPage__formRow">
                    <span>요청받을 사람</span>
                    <select
                      disabled={!canEditForm || isEditMode}
                      onChange={(event) =>
                        handleFormChange("hostUserId", event.target.value)
                      }
                      required
                      value={scheduleForm.hostUserId}
                    >
                      <option value="">구성원 선택</option>
                      {taskAssigneeOptions.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                          {member.organizationName
                            ? ` / ${member.organizationName}`
                            : ""}
                          {member.email ? ` / ${member.email}` : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                <label className="organizationPage__formRow">
                  <span>내용</span>
                  <textarea
                    disabled={!canEditForm}
                    maxLength={2000}
                    onChange={(event) =>
                      handleFormChange("description", event.target.value)
                    }
                    placeholder="업무 내용이나 확인 사항"
                    value={scheduleForm.description}
                  />
                </label>

                {isEditMode && (
                  <div className="organizationCalendar__detailMeta">
                    <span>
                      <CalendarDays size={14} aria-hidden="true" />
                      {formatScheduleRange(scheduleForm)}
                    </span>
                  </div>
                )}
              </div>

              <div className="organizationPage__modalActions">
                {isEditMode && scheduleForm.canDelete && (
                  <button
                    className="organizationPage__dangerBtn"
                    disabled={isDeleting || isSaving}
                    onClick={handleDelete}
                    type="button"
                  >
                    <Trash2 size={15} aria-hidden="true" />
                    <span>{isDeleting ? "삭제 중" : "삭제"}</span>
                  </button>
                )}
                <button
                  className="organizationPage__secondaryBtn"
                  disabled={isSaving || isDeleting}
                  onClick={handleCloseModal}
                  type="button"
                >
                  {isEditMode ? "취소" : "닫기"}
                </button>
                {canEditForm && (
                  <button
                    className="organizationPage__saveBtn"
                    disabled={isSaving || isDeleting}
                    type="submit"
                  >
                    <CalendarPlus size={15} aria-hidden="true" />
                    <span>{isSaving ? "저장 중" : "저장"}</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        {isEditCancelConfirmOpen && isEditMode && (
          <div
            className="organizationPage__modalBackdrop"
            onMouseDown={handleCloseEditCancelConfirm}
            role="presentation"
          >
            <section
              aria-describedby="organization-calendar-edit-cancel-description"
              aria-labelledby="organization-calendar-edit-cancel-title"
              aria-modal="true"
              className="organizationPage__modal organizationPage__modal--confirm"
              onMouseDown={(event) => event.stopPropagation()}
              role="alertdialog"
            >
              <div className="organizationPage__modalHeader">
                <div>
                  <h3 id="organization-calendar-edit-cancel-title">
                    일정 수정 취소
                  </h3>
                </div>
                <button
                  aria-label="확인 팝업 닫기"
                  className="organizationPage__modalClose"
                  onClick={handleCloseEditCancelConfirm}
                  type="button"
                >
                  <X size={17} aria-hidden="true" />
                </button>
              </div>

              <div
                className="organizationPage__modalBody"
                id="organization-calendar-edit-cancel-description"
              >
                <div className="organizationPage__confirmIntro">
                  <span className="organizationPage__confirmIcon">
                    <X size={20} aria-hidden="true" />
                  </span>
                  <div className="organizationPage__confirmText">
                    <strong>변경사항은 저장되지 않습니다.</strong>
                    <p>취소하시겠습니까?</p>
                  </div>
                </div>
              </div>

              <div className="organizationPage__modalActions">
                <button
                  autoFocus
                  className="organizationPage__secondaryBtn"
                  onClick={handleCloseEditCancelConfirm}
                  type="button"
                >
                  계속 수정
                </button>
                <button
                  className="organizationPage__dangerBtn"
                  onClick={handleConfirmCancelEdit}
                  type="button"
                >
                  확인
                </button>
              </div>
            </section>
          </div>
        )}
      </article>
    </>
  );
}
